import type {
  IngestRulePolicy,
  AutomationRunStatus,
  AutomationRunTrigger,
  AutomationTemplate,
  AutomationTrigger,
  DriveShareMode,
  SlackDeliveryFormat,
  IntegrationProvider,
  IntegrationScope,
  RoutingFeedbackKind,
  RoutingFeedbackStatus,
  PlaygroundAgentInfo,
  PlaygroundAgentKey,
  PlaygroundCaseItem,
  PlaygroundExpectation,
  PlaygroundGrade,
  PlaygroundMetrics,
  PlaygroundRunMetrics,
  PlaygroundRunStatus,
  PlaygroundSpec,
  RunPhase,
  RunUsage,
  UiCapabilityFlags,
  UiFeatureFlagKey,
  UiFeatureFlags,
  MetricsDashboard,
  FeedIngestion,
  FeedJourney,
} from '@repo/shared-types';

export type { MetricsDashboard, UiCapabilityFlags, UiFeatureFlagKey, UiFeatureFlags };
export type { RunPhase, RunUsage };
export type { FeedIngestion, FeedJourney };
export type {
  PlaygroundAgentInfo,
  PlaygroundAgentKey,
  PlaygroundCaseItem,
  PlaygroundExpectation,
  PlaygroundGrade,
  PlaygroundMetrics,
  PlaygroundRunMetrics,
  PlaygroundRunStatus,
  PlaygroundSpec,
};
export type { AutomationRunStatus, AutomationRunTrigger, AutomationTemplate, AutomationTrigger, DriveShareMode, SlackDeliveryFormat };
export type { IntegrationProvider, IntegrationScope };
export type { RoutingFeedbackKind, RoutingFeedbackStatus };

const BASE = '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

async function requestFormData<T>(path: string, body: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { method, credentials: 'include', body });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type MemberRole = 'ADMIN' | 'MEMBER' | 'VIEWER';

/** One row of the Ingest rules page — the router (`projectId` null) or one project. */
export interface IngestRuleScope {
  projectId: string | null;
  name: string;
  path: string;
  depth: number;
  parentId: string | null;
  version: number | null;
  policy: IngestRulePolicy;
  updatedAt: string | null;
  held7d: number;
  canEdit: boolean;
}

export interface IngestRuleOptions {
  sourceTypes: string[];
  sources: Array<{ source: string; sourceType: string; count: number; lastAt: string }>;
  senderDomains: Array<{ domain: string; count: number }>;
}

export interface IngestRuleTestVerdict {
  status: 'PASSED' | 'BLOCKED' | 'UNANSWERED';
  ruleName: string | null;
  probability: number | null;
  detail: string | null;
}

export interface IngestRuleTestResult {
  checked: number;
  blockedLive: number;
  blockedDraft: number;
  changed: Array<{
    feedItemId: string;
    title: string;
    source: string;
    sourceType: string;
    capturedAt: string;
    live: IngestRuleTestVerdict;
    draft: IngestRuleTestVerdict;
  }>;
  jevCalls: number;
  errors: string[];
}

export interface ProjectMember {
  userId: string;
  role: MemberRole;
  createdAt: string;
  user: { id: string; email: string; name: string | null };
}

export interface Project {
  id: string;
  name: string;
  /** A member-chosen emoji shown beside the name, or null. Exclusive with `iconImageAt`. */
  icon: string | null;
  /**
   * When an image was uploaded as the mark, or null for none. Presence says there is an image; the
   * value versions its URL (`api.projects.iconUrl`) so a new upload is not hidden by the cache.
   */
  iconImageAt: string | null;
  slug: string;
  /** Null only for the organization — the single root of the tree. */
  parentId: string | null;
  /** Materialized "/"-joined slug chain, e.g. "cdr/engineering/auth". */
  path: string;
  depth: number;
  tenantId: string;
  /**
   * What this project holds — **written by the system** from its own pages, not by hand.
   *
   * Null until a project has enough pages to characterise, which is also why a brand-new project is
   * not yet a routing destination. Read by people, and by the router when it places undirected
   * content.
   */
  description: string | null;
  /** When the description was last written, and from how many pages. Null if it never has been. */
  descriptionDerivedAt: string | null;
  descriptionFromPages: number | null;
  createdAt: string;
  updatedAt: string;
}

/** A project with its sub-projects — what the sidebar tree renders. */
export interface ProjectTreeNode extends Project {
  children: ProjectTreeNode[];
}

/** One level a scoped read reaches, with its share of the review queue. */
export interface ProjectScopeLevel {
  id: string;
  name: string;
  pendingChanges: number;
  /** True when the caller holds an ADMIN or MEMBER seat in this project — what "My projects" means. */
  mine?: boolean;
}

/** How far a read reaches: the whole branch, or this project alone. */
export type KbScopeMode = 'cone' | 'self';

/** One run created by an org-level ingest, labelled with where the router sent it. */
export interface RoutedRun {
  id: string;
  projectId: string;
  projectName: string;
  status: KbRunStatus;
  source: string;
  fileName: string | null;
}

export interface RoutedIngestResult {
  /** False when the caller pinned a destination, so no routing decision was made. */
  routed: boolean;
  reasoning: string;
  runs: RoutedRun[];
}

export interface SlackChannelDetail {
  id: string;
  name?: string;
  isPrivate?: boolean;
  isArchived?: boolean;
  numMembers?: number;
  topic?: string;
  error?: string;
}

/** An upcoming meeting series, for the automation trigger picker. */
export interface CalendarMeetingSeries {
  title: string;
  /** One of the calendars holding it — the meeting is grouped across all of them. */
  calendarId: string;
  /** How many watched calendars hold this same meeting — above 1 when colleagues share it. */
  calendarCount: number;
  /** How many sittings of it are ahead — 1 means a one-off. */
  occurrences: number;
  nextStartsAt: string;
  recurring: boolean;
}

/** A person in the workspace, offered as a direct-message destination. */
export interface SlackUserDetail {
  id: string;
  name: string;
  handle?: string;
  title?: string;
}

/** A watched Slack channel, as configured. */
export interface IntegrationSource {
  externalId: string;
  name: string | null;
}

/** One offered in the picker, as the provider currently reports it. */
export interface AvailableSource {
  externalId: string;
  name: string | null;
  detail?: string;
  isPrivate?: boolean;
}

export interface Integration {
  id: string;
  provider: IntegrationProvider;
  scope: IntegrationScope;
  project: { id: string; name: string; slug: string; path: string } | null;
  sources: IntegrationSource[];
  createdAt: string;
}

/** One source the sources hub collects, and where it goes here. */
export interface HubSource {
  provider: IntegrationProvider;
  externalId: string;
  name: string | null;
  /** The hub connection it comes from, e.g. whose Granola account. */
  account: string | null;
  /** False when a destination is saved here but the hub no longer collects it; null when unknown. */
  supplied: boolean | null;
  collected: { count: number; lastAt: string | null } | null;
  organization: boolean;
  projects: Array<{ id: string; name: string; slug: string }>;
}

export interface HubSubscription {
  provider: string;
  sourceExternalId: string;
}

export interface SourcesOverview {
  hub: {
    configured: boolean;
    reachable: boolean;
    error: string | null;
    appName: string | null;
    pushEnabled: boolean;
    consoleUrl: string | null;
  };
  pull: { lastPulledAt: string | null; lastError: string | null };
  sync: { inSync: boolean; missing: HubSubscription[]; extra: HubSubscription[] };
  sources: HubSource[];
}

export interface SetDestinationsInput {
  provider: IntegrationProvider;
  externalId: string;
  organization: boolean;
  projectIds: string[];
  includeHistory?: boolean;
}

/** Which integration a call addresses. The triple is the identity; there is no id to create first. */
export interface IntegrationTarget {
  provider: IntegrationProvider;
  scope: IntegrationScope;
  projectSlug?: string;
}

/** Who left or resolved a report. Enough to attribute it, never the whole user. */
export interface FeedbackPerson {
  id: string;
  name: string | null;
  email: string;
}

/** A project a report names, or null where it has since been deleted. */
export interface FeedbackProject {
  id: string;
  name: string;
  slug: string;
}

/**
 * One person's verdict on a routing decision.
 *
 * `projectId` and `segmentIndex` narrow what the verdict is *about*: a destination, one piece of a
 * split source, or — both null — the arrival as a whole. The same complaint gets made at all three
 * levels, so neither is required.
 */
export interface RoutingFeedback {
  id: string;
  runId: string;
  kind: RoutingFeedbackKind;
  comment: string | null;
  segmentIndex: number | null;
  status: RoutingFeedbackStatus;
  resolutionNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
  createdBy: FeedbackPerson;
  resolvedBy: FeedbackPerson | null;
  /** The destination this is about. Null for the decision as a whole, or a deleted project. */
  project: FeedbackProject | null;
  /** Where the reporter says it should have gone. */
  suggestedProject: FeedbackProject | null;
}

export interface RoutingFeedbackInput {
  kind: RoutingFeedbackKind;
  projectId?: string;
  segmentIndex?: number;
  suggestedProjectId?: string;
  comment?: string;
}

/** A report with enough of its decision to be read without opening the history. */
export interface RoutingFeedbackWithRun extends RoutingFeedback {
  run: {
    id: string;
    projectId: string;
    source: string;
    fileName: string | null;
    /** Whether that run chose its own destinations, which decides how its kinds should be read. */
    triage: boolean;
    queuedAt: string;
  };
}

/**
 * The admin queue and its totals.
 *
 * Totals are computed over everything rather than over the returned page, so filtering the list
 * doesn't silently move the numbers it is being judged against.
 */
export interface RoutingFeedbackQueue {
  feedback: RoutingFeedbackWithRun[];
  stats: {
    byStatus: Partial<Record<RoutingFeedbackStatus, number>>;
    byKind: Partial<Record<RoutingFeedbackKind, number>>;
    total: number;
  };
}

/** One project's waiting work and its batch window. */
export interface IngestQueueProject {
  projectId: string;
  projectName: string;
  projectSlug: string;
  mode: 'IMMEDIATE' | 'SCHEDULED';
  timeOfDay: string | null;
  timezone: string;
  /** Null for once a day; otherwise minutes between windows, counted from `timeOfDay`. */
  everyMinutes: number | null;
  batchMaxChars: number | null;
  /** When this window next lands. Null in immediate mode, or with no valid time set. */
  nextRunAt: string | null;
  segments: number;
  /** Distinct arrivals — the unit a batch is packed in, so this is the count that means "items". */
  arrivals: number;
  chars: number;
  /**
   * Of those pieces, how many are still waiting for the ingestor to decide where they belong.
   *
   * Only the organization root normally has any: content arriving with no stated destination parks
   * there until a run reads it. Everything else is pinned and stays where it is.
   */
  toPlace: number;
  /** Arrival time of the oldest waiting piece — how far behind this scope is. */
  oldest: string | null;
}

/**
 * One piece waiting in a scope's queue.
 *
 * A *segment*, not always a whole arrival: a producer that arrives in pieces writes several, and
 * `index`/`total` say so. Splitting a source by topic no longer happens here — that is a placement
 * judgement, and it happens inside the run.
 */
export interface WaitingSegment {
  id: string;
  source: string;
  sourceType: FeedSourceType;
  /** Position within its arrival. `total > 1` means the source was split. */
  index: number;
  total: number;
  /** What the segmenter called this piece. Null when its source was taken whole. */
  title: string | null;
  chars: number;
  /** This piece's own timestamp — what orders the batch, so what this list is sorted by. */
  sourceTimestamp: string;
  arrivedAt: string;
  /** Runs that took this piece and failed. Above zero it is a retry, and retries are capped. */
  attempts: number;
  /** Which run it would land in if the queue were processed now, 1-based within its own pool. */
  batch: number;
  /** Whether the run that takes this will also decide which projects it belongs in. */
  toPlace: boolean;
  /** Bigger than the budget alone, so it takes a run to itself and is split inside it. */
  oversized: boolean;
  feedItemId: string;
  excerpt: string;
}

/** A project's ingestion window as stored. Every automatic arrival consults it; nothing else does. */
export interface IngestSettings {
  ingestMode: 'IMMEDIATE' | 'SCHEDULED';
  ingestTimeOfDay: string | null;
  ingestTimezone: string;
  /** Null for once a day at `ingestTimeOfDay`; otherwise minutes between windows, counted from it. */
  ingestEveryMinutes: number | null;
  ingestBatchMaxChars: number | null;
}

/** A patch of the above: an absent field is left alone, so switching modes keeps the time. */
export type IngestSettingsInput = Partial<IngestSettings>;

export interface ProjectDeletionSummary {
  memoryEntries: number;
  stagingProposals: number;
  fileUploads: number;
  feedItems: number;
  members: number;
  /** The knowledge base cascades off the project, so deletion destroys it silently without this. */
  kbNodes: number;
  /** Non-zero blocks deletion outright — a parent must not take its descendants down with it. */
  subProjects: number;
}

export interface ProjectDeletionPreview {
  project: Project;
  summary: ProjectDeletionSummary;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  /**
   * `GET /users` returns the whole profile, so this is present there. Declared optional because the
   * pickers that filter on it (the spaces share dialog) must not offer a suspended or unapproved
   * account, and an absent value should read as "not known to be approved" rather than as approved.
   */
  accountStatus?: AccountStatus;
  createdAt: string;
  updatedAt: string;
}

// ─── Feed ─────────────────────────────────────────────────────────────────────

export type FeedSourceType =
  | 'SLACK'
  | 'FILE_UPLOAD'
  | 'MANUAL'
  | 'CLI_IMPORT'
  | 'GRANOLA'
  | 'MCP'
  /** A hand edit, recorded as a source — evidence for the person's own prose, never ingested. */
  | 'USER_EDIT'
  /** A reviewer's correction, applied by a scoped fix run that cites it. */
  | 'USER_FEEDBACK'
  /** A document that arrived alongside a conversation: linked in a message, or mailed as an attachment. */
  | 'LINKED_DOCUMENT'
  /** A message from a watched mailbox. */
  | 'EMAIL'
  /** A document from a watched Shared Drive. */
  | 'DRIVE_FILE'
  /** A person's edit to a document an automation filed in Drive — only what they changed. */
  | 'AUTOMATION_EDIT';

export interface FeedItem {
  id: string;
  projectId: string;
  content: string;
  source: string;
  sourceType: FeedSourceType;
  capturedAt: string;
  capturedById: string | null;
  batchId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  /** Where it is on its way into the memory — waiting, queued, processing, in memory… */
  ingestion?: FeedIngestion;
}

export interface FeedPage {
  items: FeedItem[];
  nextCursor: string | null;
}

/** One hit from `feed_search` — an extract and its ranking signals, never the whole item. */
export interface FeedSearchHit {
  ingestion?: FeedIngestion;
  id: string;
  projectId: string;
  source: string;
  sourceType: FeedSourceType;
  capturedAt: string;
  title: string | null;
  chars: number;
  /**
   * True when the item contains **every** content word of the query rather than merely one.
   *
   * The match is widened to a disjunction so the tail of the results is not empty, which means hits
   * are almost never zero — a query sharing one common word with the corpus returns rows. This is
   * how a reader tells "this was actually discussed" from "these share a word".
   */
  allTerms: boolean;
  /** `ts_headline` output: the matched spans wrapped in `<<` and `>>`. */
  snippet: string | null;
  queryIndex: number;
  query: string;
}

export interface FeedSearchResult {
  query: string;
  windowSince: string | null;
  windowUntil: string;
  matchCount: number;
  /** How many of `matchCount` contain every term. The number worth reading — see `allTerms`. */
  strongMatchCount: number;
  hits: FeedSearchHit[];
}

// ─── Project bootstrap ────────────────────────────────────────────────────────

export type KbNodeKind = 'FOLDER' | 'PAGE' | 'SECTION';
export type KbChangeKind = 'CREATE' | 'UPDATE' | 'DELETE' | 'MERGE' | 'MOVE';
export type KbFlagReason = 'CONFLICT' | 'UNSURE' | 'PERSONAL_INFO';
export type KbReviewStatus =
  | 'NOT_REQUIRED'
  | 'PENDING'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'KEPT_BOTH'
  /** Resolved by a fix run rewriting the page; the fix's own changes carry the open flag instead. */
  | 'SUPERSEDED';
export type KbRunStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

/** A node as it appears in the page tree — no body, just what a tree panel needs. */
export interface KbTreeNode {
  id: string;
  parentId: string | null;
  kind: KbNodeKind;
  slug: string;
  path: string;
  depth: number;
  order: number;
  title: string;
  summary: string | null;
  kbType: string | null;
  tags: string[];
  bodyChars: number;
  childCount: number;
  pendingChanges: number;
  updatedAt: string;
  children: KbTreeNode[];
}

export interface KbTree {
  nodes: KbTreeNode[];
  nodeCount: number;
  pendingChanges: number;
}

/**
 * How far a project overview reaches down the hierarchy. Distinct from `KbScopeMode`, which is the
 * org-level pages' cone/self selector — an overview never reaches *up* to its parent.
 */
export type KbStatsScope = 'self' | 'subtree';

/** One file uploaded into a project. The bucket key is deliberately not exposed. */
export interface KbUpload {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: 'UPLOADED' | 'PROCESSED' | 'FAILED';
  source: string | null;
  errorMessage: string | null;
  /** Null unless a public base URL is configured for the bucket — render a link only when set. */
  storageUrl: string | null;
  createdAt: string;
  uploadedBy: string;
  /** The file lives elsewhere (the organization root); its ingestion run filed content here. */
  routed: boolean;
  runId: string | null;
}

/** The project overview's headline numbers. */
export interface KbStats {
  scope: KbStatsScope;
  /** Projects covered — length > 1 means sub-projects are folded in. */
  projectIds: string[];
  pageCount: number;
  pendingChanges: number;
  recentChanges: number;
  recentChangesWindowDays: number;
  /** ISO timestamp, or null when nothing has ever been written here. */
  lastActivityAt: string | null;
}

export interface KbRevision {
  id: string;
  nodeId: string;
  version: number;
  title: string;
  summary: string | null;
  body: string;
  authorId: string | null;
  generatedByAI: boolean;
  changeSummary: string | null;
  sourceFeedItemIds: string[];
  runId: string | null;
  createdAt: string;
}

export interface KbChange {
  id: string;
  projectId: string;
  nodeId: string | null;
  runId: string | null;
  kind: KbChangeKind;
  beforeRevisionId: string | null;
  afterRevisionId: string | null;
  beforeTitle: string | null;
  beforeBody: string | null;
  afterTitle: string | null;
  afterBody: string | null;
  path: string;
  fromPath: string | null;
  confidence: number;
  reasoning: string;
  /** The floor this change was judged against, and the kind whose floor that was. Null on rows from before these were recorded. */
  reviewFloor: number | null;
  reviewFloorKind: KbChangeKind | null;
  /** Why it waits for a person — conflicting information, unsure, or sensitive information (PERSONAL_INFO). */
  flagReason: KbFlagReason | null;
  /** The agent's one line on what conflicts, or what the sensitive detail is (led by its category's label). */
  flagDetail: string | null;
  model: string | null;
  sourceFeedItemIds: string[];
  mergedFromPaths: string[];
  reviewStatus: KbReviewStatus;
  reviewedById: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  node: { id: string; path: string; title: string } | null;
  /** The fix run this change's reviewer started, when they answered the flag with feedback. */
  fixRunId: string | null;
  /** Only the run's confinement: set means this change came from a fix run the reviewer asked for. */
  run: {
    scopePath: string | null;
    /** When the run was asked for — for a fix run, when the feedback was sent. */
    queuedAt?: string;
    /** Set when the run is a sweep card's fix: its changes are one piece of work across pages. */
    sweepFindingId?: string | null;
    sweepFinding?: { fingerprint: string; title: string } | null;
  } | null;
  /** Which level the change happened at. Null only if the project was resolved out of scope. */
  projectName?: string | null;
}

/** One review comment: GitHub-shaped, pinned to a quoted diff line, drafted then submitted. */
export interface KbChangeComment {
  id: string;
  changeId: string;
  projectId: string;
  authorId: string;
  /** Verbatim diff line this is about; null = about the change as a whole. */
  quote: string | null;
  /** True when the quote is a line the change removed. */
  onRemoved: boolean;
  text: string;
  status: 'DRAFT' | 'SUBMITTED';
  /** Set once submitted — the fix run the comment was composed into. */
  fixRunId: string | null;
  createdAt: string;
}

/** The review queue plus the levels it spans — the filter chips are built from `levels`. */
export interface KbChangePage {
  changes: KbChange[];
  levels: ProjectScopeLevel[];
  /** Changes the caller resolved since `reviewedSince`; null when that was not asked. */
  reviewedToday?: number | null;
}

/**
 * Evidence for a span of a node's prose. The reverse of how the fact pipeline works: the agent
 * wrote the text, then recorded which span of raw source backs which span of what it wrote.
 */
export interface KbCitation {
  id: string;
  claim: string;
  /** Verbatim span of the node body this supports. */
  nodeQuote: string;
  /** Verbatim span of the raw source that backs the claim. */
  sourceQuote: string;
  confidence: number;
  createdAt: string;
  revisionId: string | null;
  runId: string | null;
  /** False once the supported sentence has been rewritten out of the body. */
  current: boolean;
  source: {
    feedItemId: string | null;
    /** The item's own title when it has one (a meeting name); the raw source string otherwise. */
    label: string;
    /** Raw `source` string, for resolving the folder/channel name — see lib/source-label.ts. */
    raw?: string | null;
    sourceType: string | null;
    capturedAt: string | null;
  };
}

export interface KbNodeCitations {
  current: KbCitation[];
  /** Citations whose sentence is gone from the body — what this page used to claim, and on what. */
  superseded: KbCitation[];
}

export interface KbCitationSource {
  citation: KbCitation & { nodePath: string | null; nodeTitle: string | null };
  source: {
    id: string;
    label: string;
    raw?: string | null;
    sourceType: string;
    capturedAt: string;
    content: string;
  } | null;
  /** Character offsets of sourceQuote inside source.content, computed server-side. */
  span: { start: number; end: number } | null;
}

export interface KbNodeDetail {
  id: string;
  projectId: string;
  parentId: string | null;
  kind: KbNodeKind;
  slug: string;
  path: string;
  depth: number;
  order: number;
  title: string;
  summary: string | null;
  body: string;
  kbType: string | null;
  tags: string[];
  currentRevisionId: string | null;
  createdAt: string;
  updatedAt: string;
  children: Array<{
    id: string;
    path: string;
    title: string;
    summary: string | null;
    kind: KbNodeKind;
    order: number;
    bodyChars: number;
  }>;
  revisions: KbRevision[];
  changes: KbChange[];
  pendingChanges: number;
  citations: KbNodeCitations;
}

/** One project an ingestion run wrote into. */
export interface RunDestination {
  projectId: string;
  projectName: string;
  projectPath: string;
  confidence: number;
  reason: string;
  /** Characters of the arrival this project was handed. */
  chars: number;
  /** True when it received the content only because nobody else claimed it. */
  fellBack: boolean;
  /** Set when this destination could not be written — its tree was left untouched. */
  error?: string;
  /**
   * What this project was actually handed, one entry per arrival it claimed anything of.
   *
   * A triage run gives each destination only the spans assigned to it, so this and the whole arrival
   * genuinely differ — and this is the only record of the cut. Excerpted; the full source is on the
   * `FeedItem`.
   */
  parts: Array<{
    itemIndex: number;
    title: string | null;
    chars: number;
    excerpt: string;
    truncated: boolean;
  }>;
  /**
   * What writing this project cost, excluding the placement pass — which is charged once for the
   * arrival and sits on `result.triage.usage`.
   *
   * Optional: a run from before cost was recorded has destinations without it.
   */
  usage?: RunUsage;
}

export interface KbRun {
  id: string;
  projectId: string;
  requestedById: string;
  source: string;
  sourceType: FeedSourceType;
  feedItemId: string | null;
  fileName: string | null;
  status: KbRunStatus;
  chunks: number;
  nodesCreated: number;
  nodesUpdated: number;
  nodesDeleted: number;
  nodesMerged: number;
  nodesMoved: number;
  flaggedForReview: number;
  /**
   * Whether this run decided its own destinations before writing.
   *
   * When true, `projectId` above is only the scope it read the content from — the organization root —
   * and `destinations` is what it actually touched.
   */
  triage: boolean;
  /**
   * Where this run wrote, and why.
   *
   * One entry either way — a pinned run records the project it was pinned to, at confidence 1 with
   * no reason, because there was no choice to explain. Null only until it has written: a run still
   * queued, one that deferred, or one from before this was recorded.
   */
  destinations: RunDestination[] | null;
  result: {
    skippedDuplicate?: boolean;
    /** The run report's one line — the list carries only this; the whole report is `kb.runReport`. */
    report?: { summary: string; attention: number };
    changes?: Array<{
      id: string;
      kind: string;
      path: string;
      reviewStatus: string;
      confidence: number;
      projectId?: string;
    }>;
    skipped?: string[];
    failed?: string[];
    /** What the guardrail kept out of the knowledge base, both phases. Absent on older runs. */
    withheld?: RunWithheld[];
    /** What the placement phase concluded, when this run did any. */
    triage?: {
      /** What deciding where this belongs cost, apart from the writing that followed. */
      usage?: RunUsage;
      reasoning: string;
      fellBack: boolean;
      notes: string[];
      discards: Array<{ itemIndex: number; chars: number; reason: string; excerpt: string }>;
      /**
       * The cut itself, one entry per assigned part — which characters of which arrival went to
       * which project. Nothing in the console reads it; it is here because it is the only place the
       * split is recorded exactly, `destinations[].parts[].excerpt` stopping at 2,000 characters.
       * Absent on runs from before it was written.
       */
      parts?: Array<{
        index: number;
        itemIndex: number;
        title: string;
        chars: number;
        span: { start: number; end: number };
        excerpt: string;
        projectIds: string[];
      }>;
      /**
       * Projects it named that placed nothing, because their confidence sat under the floor.
       *
       * The only record of them: "nothing matched" reads identically whether the agent named no
       * project at all or named the right one just under the floor, and those are opposite problems.
       */
      rejected: Array<{ projectName: string; topic: string; confidence: number; reason: string }>;
    };
  } | null;
  /** How many steps the agent took. Present on the list; the steps themselves only on the detail. */
  toolCalls: number;
  /**
   * What this run spent, as the row records it — added to after each phase, so a run still going, one
   * that deferred and one that crashed all report what they had burnt.
   *
   * Flat on the row rather than nested so the list can sort and total on it. `modelCalls` at zero on a
   * finished run means it predates this being recorded; above zero with `costUsd` at zero means the
   * provider reported no price.
   */
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  /**
   * What the agent is doing right now, denormalized onto the run so a polled list can say it
   * without a stream per row. `phase` is a `RunPhase`; both are null for a run that predates
   * streaming, and for one still sitting in the queue.
   */
  phase: RunPhase | null;
  activity: string | null;
  errorMessage: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** One entry in a run's step trail — the same shape automation runs record. */
export interface KbRunStep {
  tool: string;
  input: unknown;
  output: unknown;
  /** Which chunk of the source the agent was absorbing. 1 for everything short enough not to split. */
  chunk: number;
}

/** One piece of arriving content a run absorbed. Several of these is a batch. */
export interface KbRunItem {
  id: string;
  /** What the segmenter called it, or the source's own label. Null when it was taken whole. */
  title: string | null;
  source: string;
  sourceType: FeedSourceType;
  /** This item's own date, which is what ordered it within the batch. */
  sourceTimestamp: string;
  chars: number;
  /** Position within the source it was split from — "2 of 5" says it was split at all. */
  index: number;
  total: number;
  feedItemId: string;
  status: 'WAITING' | 'BATCHED' | 'INGESTED' | 'FAILED';
  /** Enough to recognise the piece without opening the source. */
  excerpt: string;
}

/**
 * One part of what this project was handed — the run drawer's "What it was given" record, scoped
 * to a single project. For a routed batch this is only the span assigned here, which the whole
 * arrival does not show; for a pinned run the whole item, which was never cut.
 */
export interface KbGivenPart {
  id: string;
  runId: string;
  triage: boolean;
  live: boolean;
  failed: boolean;
  /** A live routed run still deciding — nothing has been handed over yet. */
  deciding: boolean;
  sourceType: FeedSourceType;
  source: string;
  title: string | null;
  chars: number;
  /** Up to 2,000 characters of exactly what was handed over — the only record of the cut. */
  excerpt: string;
  truncated: boolean;
  /** Which arrival of the batch the part was cut from; null for a pinned whole item. */
  itemIndex: number | null;
  /** The part's item in the run's reading order — what `kb.runPartText` is asked by. */
  partItem: number | null;
  at: string;
  /** Where the material actually lives — a Granola note's own URL. Null when the source has none. */
  sourceUrl: string | null;
  /** The whole arrival the part was cut from, for reading the original in place via `feed.get`. */
  feedItemId: string | null;
}

/** The full content behind one run item — fetched on demand, see `kb.runItem`. */
/** What a run did with each section of what it was sent — see packages/knowledge-base/src/ingest/report.ts. */
export type ReportOutcome = 'kept' | 'condensed' | 'dropped' | 'set-aside' | 'withheld' | 'known';

export interface RunReportPage {
  projectId: string;
  projectName: string;
  path: string;
  action: 'created' | 'amended' | 'merged' | 'moved' | 'removed' | null;
}

export interface RunReportSection {
  itemIndex: number;
  itemTitle: string | null;
  feedItemId: string | null;
  heading: string | null;
  start: number;
  end: number;
  chars: number;
  outcome: ReportOutcome;
  action: RunReportPage['action'];
  pages: RunReportPage[];
  placedIn: string[];
  sentences: number;
  citedSentences: number;
  writtenSentences: number;
  knownSentences: number;
  cited: Array<{ start: number; end: number }>;
  written: Array<{ start: number; end: number }>;
  reason: string | null;
  reasonFrom: 'writer' | 'placement' | 'guardrail' | 'record' | null;
  withheldPassages: number;
  knownFrom: { runId: string | null; at: string } | null;
}

export interface RunReport {
  version: number;
  summary: string;
  attention: number;
  timing: { queuedAt: string; startedAt: string | null; finishedAt: string | null; waitedSeconds: number | null; seconds: number | null };
  size: { received: number; kept: number; cited: number; written: number; known: number; notWritten: number; setAside: number; withheld: number };
  counts: Record<ReportOutcome, number> & { items: number; sections: number; flags: number; rejectedCitations: number };
  sections: RunReportSection[];
  flags: Array<{
    changeId: string;
    projectName: string;
    path: string;
    reason: 'conflict' | 'sensitive' | 'unsure';
    trigger: string;
    quote: string | null;
    reviewStatus: string;
  }>;
  rejectedCitations: Array<{ path: string; claim: string; code: string; reason: string; closest: string | null }>;
  notRecorded: string[];
}

export interface KbRunItemDetail {
  id: string;
  title: string | null;
  source: string;
  sourceTimestamp: string;
  chars: number;
  content: string;
}

export interface KbRunDetail extends KbRun {
  changes: KbChange[];
  /**
   * What this run read, oldest first. One item for an immediate arrival, several for a batch —
   * otherwise invisible, since the run's counters say how many pages moved and not how much material
   * moved them.
   */
  items: KbRunItem[];
  /**
   * Null for runs that predate the trail being recorded, and for one still queued. Under a `calls`
   * key rather than a bare array, matching `AutomationRun.steps`.
   */
  steps: { calls: KbRunStep[] } | null;
}

/**
 * The three run lifecycles, which are the same four values under three Prisma enums.
 *
 * Aliased so the streaming code can be written once: `useRunStream` cares only whether a run is still
 * moving, and a type that said `KbRunStatus` would make watching an automation a type error for no
 * reason. A playground rehearsal joined the intersection without changing it, which is the evidence
 * the alias was the right shape.
 */
export type RunLifecycleStatus = KbRunStatus & AutomationRunStatus & PlaygroundRunStatus;

/** One kept retrieval — `KbAskRun`, as the API serves it. */
export interface KbAskRunRow {
  id: string;
  projectId: string;
  source: string;
  status: KbRunStatus;
  query: string;
  scope: Array<{ id: string; name: string }> | null;
  answer: string | null;
  confidence: number | null;
  citations: Array<{ index: number; path: string; projectId: string | null; title: string; summary?: string | null }> | null;
  visitedPaths: string[] | null;
  fallbackUsed: boolean;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/**
 * One stored entry of a run's activity timeline, as `GET .../runs/:id/events` returns it.
 *
 * The flattened row rather than the `RunEvent` union — the API hands back what the table holds, and
 * `runEventFromRow` in `lib/run-stream.ts` folds it back into an event so a replayed run and a live
 * one go through exactly the same code path.
 *
 * Shared by both kinds of run: `KbRunEvent` and `AutomationRunEvent` are column-for-column identical,
 * deliberately, so one row type and one renderer serve both.
 */
export interface RunEventRow {
  seq: number;
  runId: string;
  type: 'status' | 'thought' | 'tool' | 'milestone';
  chunk: number;
  eventId: string | null;
  phase: string | null;
  label: string | null;
  state: string | null;
  text: string | null;
  payload: { input?: unknown; output?: unknown; detail?: string; error?: string; chunkCount?: number } | null;
  ms: number | null;
  at: string;
}

export interface RunEventPage {
  runId: string;
  status: RunLifecycleStatus;
  phase: RunPhase | null;
  activity: string | null;
  events: RunEventRow[];
}

export interface KbCitation {
  index: number;
  /** The node's own project-relative path — never the `Project:path` label the agent cited it by. */
  path: string;
  /** Which project it lives in, so a cross-project answer can link each source correctly. */
  projectId: string | null;
  title: string;
  summary: string | null;
}

export interface KbSearchResult {
  query: string;
  answer: string;
  confidence: number | null;
  citations: KbCitation[];
  visitedPaths: string[];
  fallbackUsed: boolean;
}

export interface KbGrepHit {
  id: string;
  /** Which project's tree this lives in — paths are project-relative, so the pair addresses it. */
  projectId: string;
  path: string;
  title: string;
  matchLine: string | null;
}

export interface KbReviewResult {
  changeId: string;
  outcome: KbReviewStatus;
  nodePath: string | null;
  createdPath?: string;
  message: string;
}

// Node paths contain slashes, so they travel as a query parameter rather than a path
// segment — mirroring the KbController's own routing.
function nodeQuery(path: string): string {
  return `?path=${encodeURIComponent(path)}`;
}

// ─── Automations ──────────────────────────────────────────────────────────────

/**
 * How much of the project tree an automation's read tools may see.
 * Read-only: the document a run produces is always filed against its own project.
 */
export type AutomationReadScope = 'SELF' | 'SUBTREE' | 'CONE';

export interface Automation {
  id: string;
  projectId: string;
  createdById: string;
  name: string;
  description: string | null;
  prompt: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  timeOfDay: string | null;
  timezone: string;
  /** 0 = Sunday … 6 = Saturday. WEEKLY_ON_DAYS. */
  daysOfWeek: number[];
  /** 1–31, clamped to the last day of shorter months. MONTHLY_ON_DATE. */
  dayOfMonth: number | null;
  /** `YYYY-MM-DD` in `timezone`. ONCE_AT_DATETIME. */
  runDate: string | null;
  sourceFilter: string | null;
  /** This automation's own step budget; null = the worker default. */
  maxSteps: number | null;
  readScope: AutomationReadScope;
  postToSlack: boolean;
  /** Channels, people, or a mix. Empty = the project's first linked channel. */
  slackDestinations: string[];
  saveToDrive: boolean;
  driveFolderId: string | null;
  driveShareMode: DriveShareMode;
  /** What the Slack post carries: the whole document, or a heads-up linking its Google Doc. */
  slackFormat: SlackDeliveryFormat;
  lastTriggeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What the list endpoint returns: each automation with the tail of its run history. */
export interface AutomationWithRuns extends Automation {
  runs: AutomationRunSummary[];
  /** Every run ever, not the length of `runs` — that tail is capped at a handful. */
  runCount: number;
}

/** An automation as the org-wide list returns it to the signed-in person. */
export interface ListedAutomation extends AutomationWithRuns {
  projectName: string;
  createdByName: string | null;
  createdByMe: boolean;
  /** One of its Slack destinations is the viewer's own DM. */
  deliveredToMe: boolean;
}

export interface AutomationRunSummary {
  id: string;
  automationId: string;
  projectId: string;
  status: AutomationRunStatus;
  trigger: AutomationRunTrigger;
  requestedById: string | null;
  feedItemId: string | null;
  toolCalls: number;
  /** What this run spent — the loop plus any nested `kb_ask`. See `KbRun` for what zero means. */
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  postedToSlack: boolean;
  slackChannelId: string | null;
  slackTs: string | null;
  deliveryError: string | null;
  /** The agent's reason for keeping this run's document out of Slack and Drive; null = delivered as configured. */
  deliverySkipped: string | null;
  driveFileId: string | null;
  driveFileUrl: string | null;
  driveError: string | null;
  /** What the agent is doing right now, kept current on the row so a polled list can be alive. */
  phase: RunPhase | null;
  activity: string | null;
  errorMessage: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/** One recorded tool call — the trail behind a run's output. */
export interface AutomationRunStep {
  tool: string;
  input: unknown;
  output: unknown;
}

export interface AutomationRunDetail extends AutomationRunSummary {
  output: string | null;
  input: {
    prompt?: string;
    automationName?: string;
    triggerDescription?: string;
    triggerExcerpt?: string | null;
    localTime?: string;
    model?: string;
    queued?: boolean;
    note?: string;
  } | null;
  /** `null` until the run finishes; `calls` is empty for a run that failed before its first tool. */
  steps: { calls?: AutomationRunStep[] } | null;
  automation: { id: string; name: string; prompt: string; trigger: AutomationTrigger };
  /**
   * Every version of the Google Doc this run filed that the sources hub has delivered, oldest first:
   * the automation's own, then each person's edit. Empty when the Doc is in a folder nobody watches.
   */
  docVersions?: AutomationDocVersion[];
}

/** One version of an automation run's Google Doc — the automation's own filing, or a person's edit. */
export interface AutomationDocVersion {
  id: string;
  runId: string;
  modifiedTime: string;
  editorEmail: string | null;
  editorName: string | null;
  byAutomation: boolean;
  /** Against the version before it — what this editor did. */
  paragraphsChanged: number;
  paragraphsAdded: number;
  paragraphsRemoved: number;
  /** 0..1, against the automation's original — the run's total so far. */
  changedShare: number;
  /** The AUTOMATION_EDIT arrival filed for this edit; null for the original and formatting-only edits. */
  feedItemId: string | null;
}

/** How often people edited automations' documents in one period. See `summarizeDocEdits`. */
export interface DocEditPeriod {
  from: string;
  runs: number;
  filedToDrive: number;
  /** Runs whose Doc the sources hub has delivered — the only ones whose edits can be seen. */
  tracked: number;
  edited: number;
  /** edited ÷ tracked; null when nothing is tracked. */
  editRate: number | null;
  meanChangedShare: number | null;
  paragraphsChanged: number;
  paragraphsAdded: number;
  paragraphsRemoved: number;
  editors: number;
}

export interface DocEditMetrics {
  since: string;
  totals: DocEditPeriod;
  weeks: DocEditPeriod[];
}

/**
 * The deployment's Google credential, as the console reads it.
 *
 * There is nothing to connect and nothing to list: the credential is a service account key in the
 * worker's environment. What the UI needs is whether one is configured, which identity documents will
 * be created by, and whether mail can be read — the last needing an impersonation subject as well as
 * the key, because a service account has no mailbox of its own.
 */
export interface GoogleStatus {
  mode: 'service-account' | 'none';
  identity: string | null;
  canReadMail: boolean;
}

export interface AutomationInput {
  name: string;
  description?: string;
  prompt: string;
  trigger: AutomationTrigger;
  timeOfDay?: string;
  timezone?: string;
  daysOfWeek?: number[];
  dayOfMonth?: number;
  runDate?: string;
  sourceFilter?: string;
  maxSteps?: number | null;
  readScope?: AutomationReadScope;
  postToSlack?: boolean;
  slackDestinations?: string[];
  saveToDrive?: boolean;
  driveFolderId?: string;
  driveShareMode?: DriveShareMode;
  slackFormat?: SlackDeliveryFormat;
  enabled?: boolean;
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export type AccountStatus = 'WAITING_APPROVAL' | 'APPROVED' | 'SUSPENDED';

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  accountStatus: AccountStatus;
  isSystemAdmin: boolean;
  createdAt: string;
  /** Keys explicitly switched off — sections, groups and capabilities alike. What the editor binds to. */
  disabled: UiFeatureFlagKey[];
  /** Resolved per-feature answer, after group master switches are applied. */
  features: UiFeatureFlags;
  /** Resolved per-capability answer, after its owning section is applied. */
  capabilities: UiCapabilityFlags;
  hasOverrides: boolean;
}

/** A project as the admin access editor lists it: depth-first, with how many hold a seat in it. */
export interface AdminProject {
  id: string;
  name: string;
  slug: string;
  path: string;
  depth: number;
  parentId: string | null;
  icon: string | null;
  iconImageAt: string | null;
  members: number;
  admins: number;
}

export interface UserProjectRole {
  projectId: string;
  role: MemberRole;
  createdAt: string;
}

// ─── Agent playground ─────────────────────────────────────────────────────────

/** Who authored a config, case or run. */
export interface PlaygroundPerson {
  id: string;
  name: string | null;
  email: string;
}

export interface PlaygroundConfig {
  id: string;
  agent: PlaygroundAgentKey;
  name: string;
  description: string | null;
  spec: PlaygroundSpec;
  createdBy: PlaygroundPerson;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  runCount: number;
}

export interface PlaygroundCase {
  id: string;
  agent: PlaygroundAgentKey;
  name: string;
  notes: string | null;
  items: PlaygroundCaseItem[];
  source: string;
  chars: number;
  sourceFeedItemId: string | null;
  sourceRunId: string | null;
  expectation: PlaygroundExpectation | null;
  createdBy: PlaygroundPerson;
  createdAt: string;
  updatedAt: string;
  runCount: number;
}

/** A real arrival, as the content picker lists it. */
export interface PlaygroundFeedItem {
  id: string;
  projectId: string;
  source: string;
  sourceType: string;
  capturedAt: string;
  chars: number;
  title: string | null;
  excerpt: string;
}

export interface PlaygroundFeedItemDetail {
  id: string;
  projectId: string;
  source: string;
  sourceType: string;
  capturedAt: string;
  title: string | null;
  content: string;
}

/** One project a rehearsal placed content in. */
export interface PlaygroundDestination {
  projectId: string;
  projectName: string;
  projectPath: string;
  confidence: number;
  reason: string;
  fellBack: boolean;
  parts: Array<{ itemIndex: number; title: string | null; chars: number }>;
}

/**
 * What the agent decided.
 *
 * `rejected` is the field worth looking at first and the one an outcome cannot show: the projects it
 * named whose confidence sat under the floor. "Nothing matched" reads identically whether the agent
 * named no project at all or named the right one at 0.44, and those are opposite problems.
 */
export interface PlaygroundResult {
  destinations: PlaygroundDestination[];
  reasoning: string;
  fellBack: boolean;
  failed: boolean;
  notes: string[];
  rejected: Array<{ projectName: string; topic: string; confidence: number; reason: string }>;
  discards: Array<{ itemIndex: number; chars: number; reason: string; excerpt: string }>;
  parts: Array<{
    index: number;
    itemIndex: number;
    title: string;
    chars: number;
    span: { start: number; end: number };
    excerpt: string;
    projectIds: string[];
  }>;
  promptVersions: Record<string, string>;
  /** What the config actually did to the roster — including settings that matched no tool. */
  roster: { removed: string[]; guided: string[]; unknown: string[] };
}

/** What a retrieval rehearsal answered, and from what. */
export interface PlaygroundRetrieveResult {
  question: string;
  answer: string;
  confidence: number | null;
  citations: Array<{
    index: number;
    path: string;
    projectId: string | null;
    title: string;
    summary: string | null;
    evidence?: Array<{ claim: string; sourceQuote: string; source: string; capturedAt: string | null }>;
  }>;
  visitedPaths: string[];
  fallbackUsed: boolean;
  promptVersions: Record<string, string>;
  roster: { removed: string[]; guided: string[]; unknown: string[] };
}

/** A run as the list renders it: no step trail, no content, but every number. */
export interface PlaygroundRun {
  id: string;
  agent: PlaygroundAgentKey;
  status: PlaygroundRunStatus;
  configId: string | null;
  caseId: string | null;
  batchId: string | null;
  label: string;
  configSnapshot: PlaygroundSpec;
  expectation: PlaygroundExpectation | null;
  scopeProjectId: string;
  metrics: PlaygroundRunMetrics | null;
  grade: PlaygroundGrade | null;
  passed: boolean | null;
  toolCalls: number;
  phase: string | null;
  activity: string | null;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  errorMessage: string | null;
  requestedBy: PlaygroundPerson;
  config: { id: string; name: string } | null;
  case: { id: string; name: string } | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface PlaygroundRunDetail extends PlaygroundRun {
  /** The content exactly as it was fed in — a run reads back completely even if the case changed. */
  caseSnapshot: PlaygroundCaseItem[];
  candidateProjects: Array<{ id: string; name: string; path: string; depth: number; description: string | null }> | null;
  /** A placement for `INGEST_TRIAGE`, an answer for `KB_RETRIEVE` — read `agent` to tell which. */
  result: PlaygroundResult | PlaygroundRetrieveResult | null;
  steps: Array<{ tool: string; input: unknown; output: unknown; chunk: number }> | null;
}

export interface PlaygroundBatchSummary {
  batchId: string;
  total: number;
  completed: number;
  failed: number;
  running: number;
  graded: number;
  passed: number;
  costUsd: number;
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export const api = {
  users: {
    list: () => request<User[]>('/users'),
    get: (id: string) => request<User>(`/users/${id}`),
    create: (body: { email: string; name?: string }) =>
      request<User>('/users', { method: 'POST', body: JSON.stringify(body) }),
  },

  projects: {
    list: () => request<Project[]>('/projects'),
    tree: () => request<ProjectTreeNode[]>('/projects/tree'),
    create: (body: { name: string; slug: string; parentId?: string }) =>
      request<Project>('/projects', { method: 'POST', body: JSON.stringify(body) }),
    update: (slug: string, body: { name?: string; parentId?: string; icon?: string | null }) =>
      request<Project>(`/projects/${slug}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Same-origin and session-authenticated, so a plain <img> can load it. */
    iconUrl: (slug: string, version: string) =>
      `${BASE}/projects/${slug}/icon?v=${encodeURIComponent(new Date(version).getTime())}`,
    uploadIcon: (slug: string, image: Blob) => {
      const formData = new FormData();
      formData.append('file', image, 'icon');
      return requestFormData<Project>(`/projects/${slug}/icon`, formData, 'PUT');
    },
    removeIcon: (slug: string) => request<void>(`/projects/${slug}/icon`, { method: 'DELETE' }),
    listMembers: (slug: string) => request<ProjectMember[]>(`/projects/${slug}/members`),
    addMember: (slug: string, body: { userId: string; role?: MemberRole }) =>
      request<ProjectMember[]>(`/projects/${slug}/members`, { method: 'POST', body: JSON.stringify(body) }),
    updateMemberRole: (slug: string, userId: string, role: MemberRole) =>
      request<ProjectMember[]>(`/projects/${slug}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    removeMember: (slug: string, userId: string) =>
      request<ProjectMember[]>(`/projects/${slug}/members/${userId}`, { method: 'DELETE' }),
    deletionPreview: (slug: string) => request<ProjectDeletionPreview>(`/projects/${slug}/deletion-preview`),
    remove: (slug: string, confirm: string) =>
      request<{ deleted: boolean }>(`/projects/${slug}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  },

  calendar: {
    /** Upcoming meetings grouped into series. Empty when no calendar is connected. */
    meetings: (days = 60) => request<CalendarMeetingSeries[]>(`/calendar/meetings?days=${days}`),
  },

  slack: {
    listChannels: () => request<SlackChannelDetail[]>('/slack/channels'),
    /** Workspace members the bot can DM. Needs `users:read` to list, `im:write` to send. */
    listUsers: () => request<SlackUserDetail[]>('/slack/users'),
  },

  /** Slack, the one source still connected directly rather than through the sources hub. */
  integrations: {
    list: (projectSlug?: string) =>
      request<Integration[]>(`/integrations${projectSlug ? `?projectSlug=${encodeURIComponent(projectSlug)}` : ''}`),

    availableSources: (target: IntegrationTarget) =>
      request<AvailableSource[]>(
        `/integrations/available-sources?provider=${target.provider}&scope=${target.scope}` +
          (target.projectSlug ? `&projectSlug=${encodeURIComponent(target.projectSlug)}` : ''),
      ),

    /** Display names keyed by the raw `source` strings items carry — see lib/source-label.ts. */
    sourceLabels: () => request<Record<string, string>>('/integrations/source-labels'),

    /** Declarative: the complete set of watched channels. Creates the integration if none exists yet. */
    setSources: (body: IntegrationTarget & { externalIds: string[] }) =>
      request<Integration>('/integrations/sources', { method: 'PUT', body: JSON.stringify(body) }),

    disconnect: (id: string) =>
      request<{ disconnected: true }>(`/integrations/${id}`, { method: 'DELETE' }),
  },

  /** What the sources hub collects, and where each source goes. Saving a destination subscribes. */
  sources: {
    overview: () => request<SourcesOverview>('/sources'),

    setDestinations: (body: SetDestinationsInput) =>
      request<SourcesOverview>('/sources/destinations', { method: 'PUT', body: JSON.stringify(body) }),

    sync: () => request<SourcesOverview>('/sources/sync', { method: 'POST' }),

    pull: () => request<{ queued: true }>('/sources/pull', { method: 'POST' }),
  },

  /**
   * Feedback on what the ingestion agent did.
   *
   * Addressed by run, because one agent chooses the destinations and writes the pages — which half a
   * report is about is recorded by its `kind`, not by the row it hangs off. The routing history this
   * replaced is gone: the run itself records where it wrote, why, what each project was handed, and
   * every search it ran with its results.
   */
  ingestFeedback: {
    list: (runId: string) => request<RoutingFeedback[]>(`/ingest-feedback/runs/${runId}`),

    add: (runId: string, body: RoutingFeedbackInput) =>
      request<RoutingFeedback>(`/ingest-feedback/runs/${runId}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** Its author only. An administrator dismisses instead, which keeps the report. */
    remove: (feedbackId: string) =>
      request<{ deleted: true }>(`/ingest-feedback/${feedbackId}`, { method: 'DELETE' }),
  },

  // One read. `start`, `callback` and `disconnect` went with the OAuth flow: a key in the server's
  // environment is not something the browser can grant or revoke.
  google: {
    getStatus: () => request<GoogleStatus>('/auth/google/status'),
  },

  /** The ingestor's guardrail log — system admins only (see GuardrailController). */
  guardrail: {
    withheld: (params: { category?: string; runId?: string; projectIds?: string[]; before?: string; limit?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.category) qs.set('category', params.category);
      if (params.runId) qs.set('runId', params.runId);
      if (params.projectIds?.length) qs.set('projectIds', params.projectIds.join(','));
      if (params.before) qs.set('before', params.before);
      if (params.limit) qs.set('limit', String(params.limit));
      return request<WithheldLog>(`/guardrail/withheld?${qs}`);
    },
  },

  /** Organization-wide measurements — system admins only (see MetricsController). */
  metrics: {
    /** Every dashboard card for the week containing `week`, Monday to Sunday in `timeZone`. */
    dashboard: (params: { week?: string; timeZone?: string; weeks?: number } = {}) => {
      const qs = new URLSearchParams();
      if (params.week) qs.set('week', params.week);
      if (params.timeZone) qs.set('timeZone', params.timeZone);
      if (params.weeks) qs.set('weeks', String(params.weeks));
      return request<MetricsDashboard>(`/metrics/dashboard?${qs}`);
    },
  },

  admin: {
    listUsers: () => request<AdminUser[]>('/admin/users'),

    getUser: (userId: string) => request<AdminUser>(`/admin/users/${userId}`),

    // Declarative: send the complete set of OFF keys. Anything omitted is enabled, so a save
    // can't leave a half-applied config the way a stream of per-toggle patches could.
    setFeatures: (userId: string, body: { disabled: UiFeatureFlagKey[]; note?: string }) =>
      request<AdminUser>(`/admin/users/${userId}/features`, { method: 'PUT', body: JSON.stringify(body) }),

    setAccountStatus: (userId: string, accountStatus: AccountStatus) =>
      request<AdminUser>(`/admin/users/${userId}/account-status`, {
        method: 'PUT',
        body: JSON.stringify({ accountStatus }),
      }),

    setSystemAdmin: (userId: string, isSystemAdmin: boolean) =>
      request<AdminUser>(`/admin/users/${userId}/system-admin`, {
        method: 'PUT',
        body: JSON.stringify({ isSystemAdmin }),
      }),

    listProjects: () => request<AdminProject[]>('/admin/projects'),

    getUserProjectRoles: (userId: string) => request<UserProjectRole[]>(`/admin/users/${userId}/projects`),

    // Declarative like `setFeatures`: every project the user should hold a role in. Omitted = removed.
    setUserProjectRoles: (userId: string, roles: Array<{ projectId: string; role: MemberRole }>) =>
      request<UserProjectRole[]>(`/admin/users/${userId}/projects`, {
        method: 'PUT',
        body: JSON.stringify({ roles }),
      }),

    /** The triage queue. No filter means everything, which is what the totals are computed over. */
    listRoutingFeedback: (params: { statuses?: RoutingFeedbackStatus[]; kinds?: RoutingFeedbackKind[] } = {}) => {
      const query = new URLSearchParams();
      for (const status of params.statuses ?? []) query.append('status', status);
      for (const kind of params.kinds ?? []) query.append('kind', kind);
      const suffix = query.toString();
      return request<RoutingFeedbackQueue>(`/admin/routing-feedback${suffix ? `?${suffix}` : ''}`);
    },

    resolveRoutingFeedback: (feedbackId: string, body: { status: RoutingFeedbackStatus; resolutionNote?: string }) =>
      request<RoutingFeedback>(`/admin/routing-feedback/${feedbackId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },

  /**
   * The agent playground. System-admin only, and every route here is guarded server-side.
   *
   * Not project-scoped, unlike almost everything else in this client: a rehearsal reads the whole
   * hierarchy by design, which is the thing being tested.
   */
  playground: {
    agents: () => request<PlaygroundAgentInfo[]>('/playground/agents'),

    listConfigs: (params: { agent?: PlaygroundAgentKey; includeArchived?: boolean } = {}) => {
      const query = new URLSearchParams();
      if (params.agent) query.set('agent', params.agent);
      if (params.includeArchived) query.set('includeArchived', 'true');
      const suffix = query.toString();
      return request<PlaygroundConfig[]>(`/playground/configs${suffix ? `?${suffix}` : ''}`);
    },
    getConfig: (id: string) => request<PlaygroundConfig>(`/playground/configs/${id}`),
    createConfig: (body: { agent: PlaygroundAgentKey; name: string; description?: string | null; spec: PlaygroundSpec }) =>
      request<PlaygroundConfig>('/playground/configs', { method: 'POST', body: JSON.stringify(body) }),
    updateConfig: (
      id: string,
      body: { name?: string; description?: string | null; spec?: PlaygroundSpec; archived?: boolean },
    ) => request<PlaygroundConfig>(`/playground/configs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteConfig: (id: string) => request<{ deleted: boolean }>(`/playground/configs/${id}`, { method: 'DELETE' }),

    listCases: (agent?: PlaygroundAgentKey) =>
      request<PlaygroundCase[]>(`/playground/cases${agent ? `?agent=${agent}` : ''}`),
    getCase: (id: string) => request<PlaygroundCase>(`/playground/cases/${id}`),
    createCase: (body: {
      agent: PlaygroundAgentKey;
      name: string;
      notes?: string | null;
      items: PlaygroundCaseItem[];
      source?: string;
      sourceFeedItemId?: string | null;
      expectation?: PlaygroundExpectation | null;
    }) => request<PlaygroundCase>('/playground/cases', { method: 'POST', body: JSON.stringify(body) }),
    updateCase: (
      id: string,
      body: {
        name?: string;
        notes?: string | null;
        items?: PlaygroundCaseItem[];
        source?: string;
        expectation?: PlaygroundExpectation | null;
      },
    ) => request<PlaygroundCase>(`/playground/cases/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteCase: (id: string) => request<{ deleted: boolean }>(`/playground/cases/${id}`, { method: 'DELETE' }),

    /** Real arrivals across the whole hierarchy, for lifting into a case. */
    feed: (params: { limit?: number; cursor?: string; source?: string } = {}) => {
      const query = new URLSearchParams();
      if (params.limit) query.set('limit', String(params.limit));
      if (params.cursor) query.set('cursor', params.cursor);
      if (params.source) query.set('source', params.source);
      const suffix = query.toString();
      return request<PlaygroundFeedItem[]>(`/playground/feed${suffix ? `?${suffix}` : ''}`);
    },
    feedItem: (id: string) => request<PlaygroundFeedItemDetail>(`/playground/feed/${id}`),

    startRuns: (body: {
      agent: PlaygroundAgentKey;
      configId?: string;
      spec?: PlaygroundSpec;
      caseIds?: string[];
      items?: PlaygroundCaseItem[];
      label?: string;
      expectation?: PlaygroundExpectation | null;
      repeat?: number;
    }) =>
      request<{ batchId: string; runs: PlaygroundRun[]; baseline: boolean }>('/playground/runs', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    listRuns: (params: {
      agent?: PlaygroundAgentKey;
      configId?: string;
      caseId?: string;
      batchId?: string;
      status?: PlaygroundRunStatus[];
      limit?: number;
    } = {}) => {
      const query = new URLSearchParams();
      if (params.agent) query.set('agent', params.agent);
      if (params.configId) query.set('configId', params.configId);
      if (params.caseId) query.set('caseId', params.caseId);
      if (params.batchId) query.set('batchId', params.batchId);
      for (const status of params.status ?? []) query.append('status', status);
      if (params.limit) query.set('limit', String(params.limit));
      const suffix = query.toString();
      return request<PlaygroundRun[]>(`/playground/runs${suffix ? `?${suffix}` : ''}`);
    },
    getRun: (id: string) => request<PlaygroundRunDetail>(`/playground/runs/${id}`),
    deleteRun: (id: string) => request<{ deleted: boolean }>(`/playground/runs/${id}`, { method: 'DELETE' }),
    batch: (batchId: string) => request<PlaygroundBatchSummary>(`/playground/batches/${batchId}`),

    runEvents: (runId: string) =>
      request<{
        runId: string;
        status: PlaygroundRunStatus;
        phase: string | null;
        activity: string | null;
        events: RunEventRow[];
      }>(`/playground/runs/${runId}/events`),
    runStreamUrl: (runId: string, params: { after?: number } = {}) =>
      `${BASE}/playground/runs/${runId}/stream${params.after ? `?after=${params.after}` : ''}`,
  },

  /** Org-level ingestion, not addressed to a project. Omit projectId to let the agent choose. */
  kbIngest: {
    content: (body: { content: string; source?: string; projectId?: string }) =>
      request<RoutedIngestResult>('/kb/ingest', { method: 'POST', body: JSON.stringify(body) }),

    file: (file: File, opts?: { source?: string; projectId?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (opts?.source) formData.append('source', opts.source);
      if (opts?.projectId) formData.append('projectId', opts.projectId);
      return requestFormData<RoutedIngestResult>('/kb/ingest-file', formData);
    },
  },

  kb: {
    // Defaults to the project plus its sub-projects. Not `cone` — see the endpoint's own note:
    // a cone would add the organization's catch-all pages to every project's total.
    stats: (projectId: string, scope?: KbStatsScope) =>
      request<KbStats>(`/projects/${projectId}/kb/stats${scope ? `?scope=${scope}` : ''}`),

    tree: (projectId: string) => request<KbTree>(`/projects/${projectId}/kb/tree`),

    uploads: (projectId: string, limit?: number) =>
      request<KbUpload[]>(`/projects/${projectId}/kb/uploads${limit ? `?limit=${limit}` : ''}`),

    node: (projectId: string, path: string) =>
      request<KbNodeDetail>(`/projects/${projectId}/kb/node${nodeQuery(path)}`),

    // One call per modal open: returns the claim, both quotes, the full raw source, and the
    // offsets to highlight. The span is computed server-side so the highlight can't drift from
    // the matching logic that produced it.
    citationSource: (projectId: string, citationId: string) =>
      request<KbCitationSource>(`/projects/${projectId}/kb/citations/${citationId}/source`),

    // Fetched lazily when a review card expands, for the evidence behind that one change. The
    // card renders its own diff from the before/after already in the list response, so this is
    // read for `citations` — worth a request only once someone actually opens the change.
    changeDetail: (projectId: string, changeId: string) =>
      request<{ citations: KbNodeCitations }>(`/projects/${projectId}/kb/changes/${changeId}/diff`),

    revisions: (projectId: string, path: string) =>
      request<KbRevision[]>(`/projects/${projectId}/kb/node/revisions${nodeQuery(path)}`),

    createNode: (
      projectId: string,
      body: { parentPath?: string; title: string; summary?: string; body?: string; kind?: KbNodeKind; kbType?: string; tags?: string[] },
    ) => request<KbNodeDetail>(`/projects/${projectId}/kb/nodes`, { method: 'POST', body: JSON.stringify(body) }),

    updateNode: (
      projectId: string,
      path: string,
      body: { title?: string; summary?: string; body?: string; kbType?: string; tags?: string[]; note?: string },
    ) =>
      request<KbNodeDetail>(`/projects/${projectId}/kb/node${nodeQuery(path)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    moveNode: (projectId: string, path: string, body: { newParentPath?: string | null; newTitle?: string }) =>
      request<KbNodeDetail>(`/projects/${projectId}/kb/node/move${nodeQuery(path)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    restoreRevision: (projectId: string, path: string, revisionId: string) =>
      request<KbNodeDetail>(`/projects/${projectId}/kb/node/restore${nodeQuery(path)}`, {
        method: 'POST',
        body: JSON.stringify({ revisionId }),
      }),

    deleteNode: (projectId: string, path: string) =>
      request<{ removed: number }>(`/projects/${projectId}/kb/node${nodeQuery(path)}`, { method: 'DELETE' }),

    reorder: (projectId: string, orders: Array<{ id: string; order: number }>) =>
      request<{ reordered: number }>(`/projects/${projectId}/kb/nodes/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ orders }),
      }),

    // Fire-and-forget: returns a KbRun immediately (already terminal for a duplicate).
    // Poll api.kb.run for the outcome — the agent loop takes tens of seconds.
    ingest: (projectId: string, body: { content: string; source?: string }) =>
      request<KbRun>(`/projects/${projectId}/kb/ingest`, { method: 'POST', body: JSON.stringify(body) }),

    /**
     * Re-derives a project's description from its pages, now.
     *
     * The only way a person changes it: the field is generated, and ingestion refreshes it on a
     * throttle that is right for keeping up and wrong for the moment just after you have fixed a
     * tree or filed a new project's first pages.
     */
    refreshDescription: (projectId: string) =>
      request<{ description: string | null; pages: number }>(
        `/kb/projects/${encodeURIComponent(projectId)}/refresh-description`,
        { method: 'POST' },
      ),

    /**
     * Throws the description away rather than rewriting it.
     *
     * Needed because a refresh is seeded with the existing text, so a description that has gone stale
     * can survive being refreshed — and placement reads this field to decide what belongs in the
     * project. Clearing it makes the next derivation start from the pages alone.
     */
    clearDescription: (projectId: string) =>
      request<{ description: null; pages: null }>(
        `/kb/projects/${encodeURIComponent(projectId)}/description`,
        { method: 'DELETE' },
      ),

    /** What is waiting for a batch window, per project — org-level, since batching is per project. */
    ingestQueue: () => request<{ projects: IngestQueueProject[] }>('/kb/ingest-queue'),

    /**
     * The pieces waiting in one project, with the batch each would land in.
     *
     * Fetched per project when a row is opened rather than with the list above: every waiting item
     * carries an excerpt, and a day's backlog across the tree is a lot of response to render counts.
     */
    waitingSegments: (projectId: string) =>
      request<{ budget: number; segments: WaitingSegment[] }>(
        `/kb/ingest-queue/${encodeURIComponent(projectId)}/segments`,
      ),

    /**
     * Absorbs what is waiting now. Omit the project to drain every project that has anything; pass
     * `pool` + `batches` to take only the oldest N runs of one pool.
     */
    processIngestQueue: (
      projectId?: string,
      options: { pool?: 'place' | 'pinned'; batches?: number } = {},
    ) => {
      const query = new URLSearchParams();
      if (projectId) query.set('projectId', projectId);
      if (options.pool) query.set('pool', options.pool);
      if (options.batches) query.set('batches', String(options.batches));
      const qs = query.toString();
      return request<{ runs: number; segments: number; projects: number }>(
        `/kb/ingest-queue/process${qs ? `?${qs}` : ''}`,
        { method: 'POST' },
      );
    },

    /** Take one waiting feed item out of its batch and absorb it now. */
    processFeedItem: (feedItemId: string) =>
      request<{ runs: Array<{ runId: string; projectId: string }>; segments: number }>(
        `/kb/ingest-queue/items/${encodeURIComponent(feedItemId)}/process`,
        { method: 'POST' },
      ),

    updateIngestSettings: (projectId: string, body: IngestSettingsInput) =>
      request<IngestSettings>(`/kb/projects/${projectId}/ingest-settings`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    ingestFile: (projectId: string, file: File, fields?: { source?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (fields?.source) formData.append('source', fields.source);
      return requestFormData<KbRun>(`/projects/${projectId}/kb/ingest-file`, formData);
    },

    runs: (
      projectId: string,
      params?: {
        scope?: KbScopeMode;
        limit?: number;
        offset?: number;
        /**
         * Include runs that wrote into this scope as well as ones read from it — what one project's
         * activity has to ask, since a routed arrival is read from the organization root and would
         * otherwise be invisible in the project it changed.
         */
        includeDestinations?: boolean;
      },
    ) => {
      const qs = new URLSearchParams();
      if (params?.scope) qs.set('scope', params.scope);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      if (params?.includeDestinations) qs.set('includeDestinations', 'true');
      return request<KbRun[]>(`/projects/${projectId}/kb/runs?${qs}`);
    },

    run: (projectId: string, runId: string) => request<KbRunDetail>(`/projects/${projectId}/kb/runs/${runId}`),

    /** One item a run read, in full. The run carries only a 320-char excerpt of each. */
    /**
     * The whole text of one part a run handed a project, or one it discarded — the run keeps only an
     * excerpt. Null when it cannot be rebuilt; `exact: false` when read forward from the excerpt.
     */
    runPartText: (
      projectId: string,
      runId: string,
      part: { destination: string; item: number } | { discard: number },
    ) => {
      const qs = new URLSearchParams();
      if ('discard' in part) qs.set('discard', String(part.discard));
      else {
        qs.set('destination', part.destination);
        qs.set('item', String(part.item));
      }
      return request<{
        text: string;
        exact: boolean;
        chars: number;
        /** One range per passage, into the original arrival — what a highlight lights up. */
        ranges: Array<{ start: number; end: number }>;
      } | null>(
        `/projects/${projectId}/kb/runs/${runId}/part-text?${qs}`,
      );
    },
    /** What the run did with each section of what it was sent. */
    runReport: (projectId: string, runId: string) => request<RunReport>(`/projects/${projectId}/kb/runs/${runId}/report`),
    runItem: (projectId: string, runId: string, itemId: string) =>
      request<KbRunItemDetail>(`/projects/${projectId}/kb/runs/${runId}/items/${itemId}`),

    /** What this project was handed, part by part — the dashboard's Activity list. */
    absorbedItems: (projectId: string, limit?: number) =>
      request<KbGivenPart[]>(`/projects/${projectId}/kb/absorbed-items${limit ? `?limit=${limit}` : ''}`),

    /**
     * The stored activity timeline. What a finished run renders from, and what a live one renders
     * before its EventSource takes over — see `useRunStream`, which does both.
     */
    runEvents: (projectId: string, runId: string, params?: { after?: number }) => {
      const qs = new URLSearchParams();
      if (params?.after !== undefined) qs.set('after', String(params.after));
      return request<RunEventPage>(`/projects/${projectId}/kb/runs/${runId}/events?${qs}`);
    },

    /**
     * The SSE endpoint's URL, for `new EventSource(...)`.
     *
     * A URL rather than a wrapper because EventSource is the client here: the browser handles
     * reconnection and `Last-Event-ID` resume itself, which is most of what this would otherwise
     * have to reimplement. Auth needs nothing — the session is an HTTP-only cookie on a same-origin
     * path, which EventSource sends.
     */
    runStreamUrl: (projectId: string, runId: string, params?: { after?: number }) => {
      const qs = new URLSearchParams();
      if (params?.after !== undefined) qs.set('after', String(params.after));
      return `${BASE}/projects/${projectId}/kb/runs/${runId}/stream?${qs}`;
    },

    search: (projectId: string, body: { query: string; scope?: KbScopeMode; askId?: string }) =>
      request<KbSearchResult>(`/projects/${projectId}/kb/search`, { method: 'POST', body: JSON.stringify(body) }),

    /**
     * The SSE endpoint for one ask, addressed by the id the page minted and sent with `search`.
     * Same EventSource-not-wrapper reasoning as `runStreamUrl` above.
     */
    askStreamUrl: (projectId: string, askId: string, params?: { after?: number }) => {
      const qs = new URLSearchParams();
      if (params?.after !== undefined) qs.set('after', String(params.after));
      return `${BASE}/projects/${projectId}/kb/ask/${askId}/stream?${qs}`;
    },

    /** Past asks across the scope — what was asked, what it answered, and what it cost. */
    askRuns: (projectId: string, params?: { scope?: KbScopeMode; limit?: number; cursor?: string }) => {
      const qs = new URLSearchParams();
      if (params?.scope) qs.set('scope', params.scope);
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.cursor) qs.set('cursor', params.cursor);
      return request<KbAskRunRow[]>(`/projects/${projectId}/kb/ask-runs?${qs}`);
    },

    /** One ask's stored timeline, in the shape every run-events endpoint shares. */
    askRunEvents: (projectId: string, askId: string, params?: { after?: number }) => {
      const qs = new URLSearchParams();
      if (params?.after !== undefined) qs.set('after', String(params.after));
      return request<RunEventPage>(`/projects/${projectId}/kb/ask/${askId}/events?${qs}`);
    },

    grep: (projectId: string, body: { pattern: string; regex?: boolean; limit?: number; scope?: KbScopeMode }) =>
      request<KbGrepHit[]>(`/projects/${projectId}/kb/grep`, { method: 'POST', body: JSON.stringify(body) }),

    changes: (
      projectId: string,
      params?: {
        reviewStatus?: KbReviewStatus;
        scope?: KbScopeMode;
        /** Narrows to specific levels. Ids outside the scope are ignored server-side. */
        projectIds?: string[];
        /** Narrows to projects where the caller is an ADMIN or MEMBER — the queue's default lens. */
        mine?: boolean;
        limit?: number;
        offset?: number;
        /** The reviewer's local midnight — adds `reviewedToday` to the page. */
        reviewedSince?: Date;
      },
    ) => {
      const qs = new URLSearchParams();
      if (params?.reviewStatus) qs.set('reviewStatus', params.reviewStatus);
      if (params?.reviewedSince) qs.set('reviewedSince', params.reviewedSince.toISOString());
      if (params?.scope) qs.set('scope', params.scope);
      if (params?.projectIds?.length) qs.set('projectIds', params.projectIds.join(','));
      if (params?.mine) qs.set('mine', 'true');
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      return request<KbChangePage>(`/projects/${projectId}/kb/changes?${qs}`);
    },

    accept: (projectId: string, changeId: string, body?: { reviewNote?: string }) =>
      request<KbReviewResult>(`/projects/${projectId}/kb/changes/${changeId}/accept`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),

    reject: (projectId: string, changeId: string, body?: { reviewNote?: string }) =>
      request<KbReviewResult>(`/projects/${projectId}/kb/changes/${changeId}/reject`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),

    keepBoth: (projectId: string, changeId: string, body?: { reviewNote?: string }) =>
      request<KbReviewResult>(`/projects/${projectId}/kb/changes/${changeId}/keep-both`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),

    // The edit *is* the review: applies the changes to the page (recorded as the reviewer's own
    // source, like any hand edit) and marks the change accepted, in one call.
    resolveChangeByEdit: (
      projectId: string,
      changeId: string,
      body: { title?: string; summary?: string; body?: string; note?: string },
    ) =>
      request<KbReviewResult>(`/projects/${projectId}/kb/changes/${changeId}/resolve-by-edit`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // Submits the review as one fix run: every draft comment plus the optional overall note,
    // composed into a single dated source and a queued run confined to the page.
    fixChange: (projectId: string, changeId: string, feedback?: string) =>
      request<{ runId: string; skippedDuplicate: boolean; message: string }>(
        `/projects/${projectId}/kb/changes/${changeId}/fix`,
        { method: 'POST', body: JSON.stringify(feedback?.trim() ? { feedback } : {}) },
      ),

    changeComments: (projectId: string, changeId: string) =>
      request<KbChangeComment[]>(`/projects/${projectId}/kb/changes/${changeId}/comments`),

    addChangeComment: (
      projectId: string,
      changeId: string,
      body: { text: string; quote?: string; onRemoved?: boolean },
    ) =>
      request<KbChangeComment>(`/projects/${projectId}/kb/changes/${changeId}/comments`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    deleteChangeComment: (projectId: string, changeId: string, commentId: string) =>
      request<{ deleted: string }>(`/projects/${projectId}/kb/changes/${changeId}/comments/${commentId}`, {
        method: 'DELETE',
      }),

    // The same scoped fix run, addressed by page rather than by change.
    fixNode: (projectId: string, path: string, feedback: string) =>
      request<{ runId: string; skippedDuplicate: boolean; message: string }>(
        `/projects/${projectId}/kb/node/fix${nodeQuery(path)}`,
        { method: 'POST', body: JSON.stringify({ feedback }) },
      ),

    reindex: (projectId: string, batchSize?: number) =>
      request<{ scanned: number; embedded: number }>(`/projects/${projectId}/kb/reindex`, {
        method: 'POST',
        body: JSON.stringify(batchSize ? { batchSize } : {}),
      }),
  },

  /**
   * Org-level automation management. An automation is *homed* at a project (a field on it), but
   * it is managed centrally — see AutomationsGlobalController.
   */
  orgAutomations: {
    templates: () => request<AutomationTemplate[]>('/automations/templates'),
    list: () => request<ListedAutomation[]>('/automations'),
    get: (id: string) => request<Automation>(`/automations/${id}`),
    create: (body: AutomationInput & { projectId: string }) =>
      request<Automation>('/automations', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<AutomationInput> & { projectId?: string }) =>
      request<Automation>(`/automations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    remove: (id: string) => request<{ deleted: string }>(`/automations/${id}`, { method: 'DELETE' }),
    runNow: (id: string) => request<AutomationRunSummary>(`/automations/${id}/run`, { method: 'POST' }),
    runs: (id: string, params?: { limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      return request<AutomationRunSummary[]>(`/automations/${id}/runs?${qs}`);
    },
    /** How often people edited this automation's Google Docs, overall and week by week. */
    editMetrics: (id: string, weeks = 12) => request<DocEditMetrics>(`/automations/${id}/edit-metrics?weeks=${weeks}`),
    /** The same across every automation that files to Drive, with each automation's totals. */
    editMetricsAll: (weeks = 12) =>
      request<DocEditMetrics & { automations: Array<{ automationId: string; name: string; projectName: string; totals: DocEditPeriod }> }>(
        `/automations/edit-metrics?weeks=${weeks}`,
      ),
    /** Run detail still lives on the home project's route — the run *is* project-scoped data. */
    run: (projectId: string, runId: string) =>
      request<AutomationRunDetail>(`/projects/${projectId}/automation-runs/${runId}`),

    /**
     * The stored activity timeline for one automation run — same shape, same renderer and same hook
     * as the ingestion runs' (see `useRunStream`). Only the path differs.
     */
    runEvents: (projectId: string, runId: string, params?: { after?: number }) => {
      const qs = new URLSearchParams();
      if (params?.after !== undefined) qs.set('after', String(params.after));
      return request<RunEventPage>(`/projects/${projectId}/automation-runs/${runId}/events?${qs}`);
    },

    /** The SSE endpoint's URL, for `new EventSource(...)`. See the KB equivalent for why a URL. */
    runStreamUrl: (projectId: string, runId: string, params?: { after?: number }) => {
      const qs = new URLSearchParams();
      if (params?.after !== undefined) qs.set('after', String(params.after));
      return `${BASE}/projects/${projectId}/automation-runs/${runId}/stream?${qs}`;
    },
  },

  automations: {
    templates: (projectId: string) =>
      request<AutomationTemplate[]>(`/projects/${projectId}/automations/templates`),

    list: (projectId: string) => request<AutomationWithRuns[]>(`/projects/${projectId}/automations`),

    get: (projectId: string, id: string) => request<Automation>(`/projects/${projectId}/automations/${id}`),

    create: (projectId: string, body: AutomationInput) =>
      request<Automation>(`/projects/${projectId}/automations`, { method: 'POST', body: JSON.stringify(body) }),

    update: (projectId: string, id: string, body: Partial<AutomationInput>) =>
      request<Automation>(`/projects/${projectId}/automations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    remove: (projectId: string, id: string) =>
      request<{ deleted: string }>(`/projects/${projectId}/automations/${id}`, { method: 'DELETE' }),

    // Returns a QUEUED run immediately; poll api.automations.run for the output.
    runNow: (projectId: string, id: string) =>
      request<AutomationRunSummary>(`/projects/${projectId}/automations/${id}/run`, { method: 'POST' }),

    runs: (projectId: string, id: string, params?: { limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      return request<AutomationRunSummary[]>(`/projects/${projectId}/automations/${id}/runs?${qs}`);
    },

    projectRuns: (projectId: string, params?: { limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.offset) qs.set('offset', String(params.offset));
      return request<AutomationRunSummary[]>(`/projects/${projectId}/automation-runs?${qs}`);
    },

    run: (projectId: string, runId: string) =>
      request<AutomationRunDetail>(`/projects/${projectId}/automation-runs/${runId}`),
  },

  ingestRules: {
    list: () => request<{ scopes: IngestRuleScope[]; systemAdmin: boolean }>('/ingest-rules'),
    options: () => request<IngestRuleOptions>('/ingest-rules/options'),
    /** Save all: every changed scope as a new version, or nothing. */
    save: (changes: Array<{ projectId: string | null; baseVersion: number | null; policy: IngestRulePolicy }>) =>
      request<{ saved: Array<{ projectId: string | null; version: number }> }>('/ingest-rules', {
        method: 'PUT',
        body: JSON.stringify({ changes }),
      }),
    test: (projectId: string | null, policy: IngestRulePolicy) =>
      request<IngestRuleTestResult>('/ingest-rules/test', { method: 'POST', body: JSON.stringify({ projectId, policy }) }),
  },

  feed: {
    /** An item an ingest rule held, re-filed past the rules once. */
    release: (id: string) =>
      request<{ released: boolean; runIds: string[] }>(`/feed/${encodeURIComponent(id)}/release`, { method: 'POST' }),
    /** Omit `projectId` for the whole organization — the default the Feed page opens with. */
    list: (params?: {
      projectId?: string | null;
      scope?: KbScopeMode;
      sourceType?: string;
      source?: string;
      cursor?: string;
      limit?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params?.projectId) qs.set('projectId', params.projectId);
      if (params?.scope) qs.set('scope', params.scope);
      if (params?.sourceType) qs.set('sourceType', params.sourceType);
      if (params?.source) qs.set('source', params.source);
      if (params?.cursor) qs.set('cursor', params.cursor);
      if (params?.limit) qs.set('limit', String(params.limit));
      return request<FeedPage>(`/feed?${qs}`);
    },
    /**
     * The automation agent's own `feed_search`, for auditing what a run could have found.
     *
     * Separate from `list` because the two answer differently: a listing is a cursored page, a
     * search is a ranked top-N with a relevance snippet and the all-terms flag.
     */
    search: (params: {
      q: string;
      projectId?: string | null;
      scope?: KbScopeMode;
      sourceType?: string;
      source?: string;
      /** Days back. Omit for everything captured. */
      days?: number;
      limit?: number;
    }) => {
      const qs = new URLSearchParams();
      qs.set('q', params.q);
      if (params.projectId) qs.set('projectId', params.projectId);
      if (params.scope) qs.set('scope', params.scope);
      if (params.sourceType) qs.set('sourceType', params.sourceType);
      if (params.source) qs.set('source', params.source);
      if (params.days) qs.set('days', String(params.days));
      if (params.limit) qs.set('limit', String(params.limit));
      return request<FeedSearchResult>(`/feed/search?${qs}`);
    },
    /** Current states for items still on their way — polled while any visible item is moving. */
    status: (ids: string[]) => request<Record<string, FeedIngestion>>(`/feed/status?ids=${ids.map(encodeURIComponent).join(',')}`),
    /** Where one item went: its runs, and the projects and pages written from it. */
    journey: (id: string) => request<FeedJourney>(`/feed/${encodeURIComponent(id)}/journey`),
    sources: (params?: { projectId?: string | null; scope?: KbScopeMode }) => {
      const qs = new URLSearchParams();
      if (params?.projectId) qs.set('projectId', params.projectId);
      if (params?.scope) qs.set('scope', params.scope);
      return request<string[]>(`/feed/sources?${qs}`);
    },
    get: (id: string) => request<FeedItem>(`/feed/${id}`),
  },
};

// ─── KB Sweeps ───────────────────────────────────────────────────────────────
//
// The sweep agent's runs over the store and the cards it files, with the thread on each card. Its
// own export rather than a key on `api` because it arrived with the sweeps page and reads more
// cleanly as one module.

export type KbSweepStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
export type KbSweepTrigger = 'MANUAL' | 'SCHEDULED';
export type KbSweepFindingKind = 'MISPLACED' | 'DUPLICATE' | 'NEAR_DUPLICATE' | 'CONTRADICTION' | 'STRUCTURE' | 'STALE' | 'OTHER';
export type KbSweepSeverity = 'HIGH' | 'MEDIUM' | 'LOW';
export type KbSweepFindingStatus = 'OPEN' | 'FIXING' | 'FIXED' | 'RESOLVED' | 'DISMISSED';
export type KbSweepMessageRole = 'USER' | 'AGENT' | 'SYSTEM';

/** A spend with how many runs (or sweeps) it sums — "$4.10 on 6 sweeps", not a bare figure. */
export interface KbSweepUsageTotal extends RunUsage {
  runs: number;
}

export interface KbSweep {
  id: string;
  projectId: string;
  projectName: string | null;
  requestedById: string | null;
  trigger: KbSweepTrigger;
  status: KbSweepStatus;
  instruction: string | null;
  scope: Array<{ id: string; name: string }> | null;
  report: string | null;
  findingsFiled: number;
  findingsConfirmed: number;
  findingsResolved: number;
  pagesRead: number;
  toolCalls: number;
  phase: string | null;
  activity: string | null;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  cancelRequested: boolean;
  errorMessage: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface KbSweepFindingPage {
  nodeId: string;
  projectId: string;
  projectName: string;
  path: string;
  address: string;
  title: string;
}

export interface KbSweepFinding {
  id: string;
  shortId: string;
  sweepId: string;
  lastSweepId: string | null;
  seenCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  projectId: string;
  projectIds: string[];
  nodeIds: string[];
  kind: KbSweepFindingKind;
  severity: KbSweepSeverity;
  title: string;
  summary: string;
  pages: KbSweepFindingPage[];
  evidence: string[];
  proposedAction: string | null;
  suggestedProjectId: string | null;
  suggestedProjectName: string | null;
  suggestedPath: string | null;
  status: KbSweepFindingStatus;
  fixedAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolveNote: string | null;
  dismissedById: string | null;
  dismissedAt: string | null;
  dismissNote: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessageAt: string | null;
  /** What the fix runs dispatched from this card spent, and how many there were. */
  fixUsage: KbSweepUsageTotal;
}

export interface KbSweepMessage {
  id: string;
  role: KbSweepMessageRole;
  authorId: string | null;
  body: string;
  runIds: string[];
  createdAt: string;
}

export interface KbSweepFixRun {
  id: string;
  projectId: string;
  projectName: string | null;
  status: KbRunStatus;
  fileName: string | null;
  scopePaths: string[];
  nodesCreated: number;
  nodesUpdated: number;
  nodesDeleted: number;
  nodesMerged: number;
  nodesMoved: number;
  flaggedForReview: number;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  activity: string | null;
  errorMessage: string | null;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface KbSweepFindingDetail extends KbSweepFinding {
  messages: KbSweepMessage[];
  fixRuns: KbSweepFixRun[];
  pagesNow: Array<{ nodeId: string; exists: boolean; projectId: string | null; path: string | null; title: string | null; chars: number | null; updatedAt: string | null }>;
}

export interface KbSweepCounts {
  high: number;
  medium: number;
  low: number;
  total: number;
  liveSweeps: number;
  /** What the last `days` days cost: the sweeps' own spend, and the fix runs their cards dispatched. */
  spend: { days: number; sweeps: KbSweepUsageTotal; fixes: KbSweepUsageTotal };
}

export interface KbSweepSettings {
  sweepMode: 'OFF' | 'DAILY';
  sweepTimeOfDay: string | null;
  sweepTimezone: string;
  sweepInstruction: string | null;
  lastSweptAt: string | null;
}

export interface KbSweepFindingQuery {
  scope?: KbScopeMode;
  status?: KbSweepFindingStatus | 'ALL';
  kind?: KbSweepFindingKind | '';
  severity?: KbSweepSeverity | '';
  sweepId?: string;
  limit?: number;
  offset?: number;
}

export const kbSweepsApi = {
  summary: (projectId: string, scope?: KbScopeMode) =>
    request<KbSweepCounts>(`/projects/${projectId}/kb/sweeps/summary${scope ? `?scope=${scope}` : ''}`),
  list: (projectId: string, params: { scope?: KbScopeMode; limit?: number; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.scope) qs.set('scope', params.scope);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    return request<KbSweep[]>(`/projects/${projectId}/kb/sweeps?${qs}`);
  },
  start: (projectId: string, instruction?: string) =>
    request<KbSweep>(`/projects/${projectId}/kb/sweeps`, { method: 'POST', body: JSON.stringify(instruction?.trim() ? { instruction: instruction.trim() } : {}) }),
  get: (projectId: string, sweepId: string) =>
    request<{ sweep: KbSweep; findings: KbSweepFinding[]; fixUsage: KbSweepUsageTotal; totalUsage: RunUsage }>(`/projects/${projectId}/kb/sweeps/${sweepId}`),
  cancel: (projectId: string, sweepId: string) =>
    request<{ outcome: 'cancelled' | 'requested' | 'not-live'; sweep: KbSweep }>(`/projects/${projectId}/kb/sweeps/${sweepId}/cancel`, { method: 'POST', body: '{}' }),
  events: (projectId: string, sweepId: string, params?: { after?: number }) => {
    const qs = new URLSearchParams();
    if (params?.after !== undefined) qs.set('after', String(params.after));
    return request<RunEventPage>(`/projects/${projectId}/kb/sweeps/${sweepId}/events?${qs}`);
  },
  streamUrl: (projectId: string, sweepId: string, params?: { after?: number }) => {
    const qs = new URLSearchParams();
    if (params?.after !== undefined) qs.set('after', String(params.after));
    return `${BASE}/projects/${projectId}/kb/sweeps/${sweepId}/stream?${qs}`;
  },

  findings: (projectId: string, query: KbSweepFindingQuery = {}) => {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    }
    return request<{ findings: KbSweepFinding[]; counts: KbSweepCounts }>(`/projects/${projectId}/kb/sweep-findings?${qs}`);
  },
  finding: (projectId: string, id: string) => request<KbSweepFindingDetail>(`/projects/${projectId}/kb/sweep-findings/${id}`),
  message: (projectId: string, id: string, body: string) =>
    request<{ findingId: string; message: KbSweepMessage; runs: Array<{ runId: string; projectId: string; projectName: string; destination: boolean; skippedDuplicate: boolean }> }>(
      `/projects/${projectId}/kb/sweep-findings/${id}/messages`,
      { method: 'POST', body: JSON.stringify({ body }) },
    ),
  dismiss: (projectId: string, id: string, note?: string) =>
    request<KbSweepFinding>(`/projects/${projectId}/kb/sweep-findings/${id}/dismiss`, { method: 'POST', body: JSON.stringify({ note }) }),
  resolve: (projectId: string, id: string, note?: string) =>
    request<KbSweepFinding>(`/projects/${projectId}/kb/sweep-findings/${id}/resolve`, { method: 'POST', body: JSON.stringify({ note }) }),
  reopen: (projectId: string, id: string) =>
    request<KbSweepFinding>(`/projects/${projectId}/kb/sweep-findings/${id}/reopen`, { method: 'POST', body: '{}' }),

  settings: (projectId: string) => request<KbSweepSettings>(`/projects/${projectId}/kb/sweep-settings`),
  updateSettings: (projectId: string, body: Partial<KbSweepSettings>) =>
    request<KbSweepSettings>(`/projects/${projectId}/kb/sweep-settings`, { method: 'PATCH', body: JSON.stringify(body) }),
};

// ─── Slack chat ─────────────────────────────────────────────────────────────

export type ChatTurnStatus = 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
export type ChatFeedback = 'UP' | 'DOWN';

export interface ChatPerson {
  id: string;
  name: string | null;
  email: string;
}

/** One conversation with the Slack bot, as the list shows it. */
export interface ChatSessionRow {
  id: string;
  channelId: string;
  threadTs: string;
  isDirect: boolean;
  projectId: string | null;
  project: { id: string; name: string; slug: string } | null;
  createdAt: string;
  lastTurnAt: string;
  turnCount: number;
  firstQuestion: string | null;
  lastStatus: ChatTurnStatus | null;
  askers: ChatPerson[];
  feedback: { up: number; down: number };
  costUsd: number;
}

export interface ChatActionRow {
  id: string;
  kind: 'INGEST' | 'AUTOMATION_CREATE';
  status: 'PROPOSED' | 'APPROVED' | 'CANCELLED' | 'FAILED';
  payload: Record<string, unknown>;
  resultRef: string | null;
  resultMessage: string | null;
  decidedAt: string | null;
  createdAt: string;
}

/** One message to the bot and its answer. */
export interface ChatTurnRow {
  id: string;
  sessionId: string;
  question: string;
  answer: string | null;
  status: ChatTurnStatus;
  errorMessage: string | null;
  phase: RunPhase | null;
  activity: string | null;
  feedback: ChatFeedback | null;
  feedbackAt: string | null;
  feedbackNote: string | null;
  queuedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  reasoningTokens: number;
  modelCalls: number;
  user: ChatPerson | null;
  actions: ChatActionRow[];
  /** What was attached to the message and could be read — bodies are not sent to the browser. */
  documents: { documents: Array<{ title: string; source: string; chars: number }>; skipped: Record<string, number> } | null;
}

/**
 * The console's read side of the Slack chat. Global, not project-scoped: a conversation reads across
 * whichever projects its asker belongs to, and access is "did you ask in it" (or system admin).
 */
export const chatApi = {
  sessions: (params: { everyone?: boolean; limit?: number; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.everyone) qs.set('everyone', 'true');
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    return request<{ sessions: ChatSessionRow[]; canSeeEveryone: boolean }>(`/chat/sessions?${qs}`);
  },
  session: (sessionId: string) =>
    request<{ session: ChatSessionRow; turns: ChatTurnRow[] }>(`/chat/sessions/${sessionId}`),
  turnEvents: (turnId: string, params?: { after?: number }) => {
    const qs = new URLSearchParams();
    if (params?.after !== undefined) qs.set('after', String(params.after));
    return request<RunEventPage>(`/chat/turns/${turnId}/events?${qs}`);
  },
  turnStreamUrl: (turnId: string, params?: { after?: number }) => {
    const qs = new URLSearchParams();
    if (params?.after !== undefined) qs.set('after', String(params.after));
    return `${BASE}/chat/turns/${turnId}/stream?${qs}`;
  },
};

/** One passage the ingestor's guardrail kept out of the knowledge base — see `KbWithheldPassage`. */
export interface WithheldLogEntry {
  id: string;
  createdAt: string;
  runId: string;
  /** PLACEMENT: cut out before any project saw it. WRITING: the writer declared it and was held to it. */
  phase: 'PLACEMENT' | 'WRITING';
  project: { id: string; name: string; path: string };
  feedItem: { id: string; source: string; sourceType: string; capturedAt: string } | null;
  category: string;
  categoryLabel: string;
  confidence: number;
  reason: string;
  text: string;
  itemIndex: number | null;
  span: { start: number; end: number } | null;
}

export interface WithheldLog {
  entries: WithheldLogEntry[];
  nextBefore: string | null;
  byCategory: Array<{ category: string; label: string; count: number }>;
}

/** What a run row says the guardrail withheld — categories and reasons, never the text. */
export interface RunWithheld {
  phase: 'PLACEMENT' | 'WRITING';
  projectId: string;
  itemIndex: number | null;
  category: string;
  confidence: number;
  reason: string;
  chars: number;
}

// ─── Personal spaces ─────────────────────────────────────────────────────────
//
// Each person's private tree of folders and files, and the folders others have shared with them.
// Not the knowledge base: nothing here is ingested, searched by `kb_*`, or read by an agent unless
// the person asks their own MCP client to. Its own export, like the sweeps and chat clients, because
// it arrived with its page and carries its own error shape.

export type SpaceAccess = 'owner' | 'editor' | 'viewer';
export type SpaceIndexStatus = 'NONE' | 'PENDING' | 'INDEXED' | 'PARTIAL' | 'UNSUPPORTED' | 'TOO_LARGE' | 'FAILED';
export type SpaceChannel = 'console' | 'mcp';
export type SpaceOnConflict = 'fail' | 'replace' | 'rename';

export interface SpaceSummary {
  id: string;
  handle: string;
  address: '~/';
  rootFolderId: string;
  ownerEmail: string;
  quotaBytes: number;
  usedBytes: number;
  /** False when file storage is not configured: folders and sharing still work, uploads and downloads answer 503. */
  uploadsEnabled: boolean;
  maxFileBytes: number;
  maxTextWriteBytes: number;
  trashDays: number;
}

export interface SpaceNodeView {
  type: 'folder' | 'file';
  id: string;
  name: string;
  /** As the caller sees it: `~/a/b/` in your own space, `@alice/team-a/x/` in one shared with you. Folders end in `/`. */
  address: string;
  spaceHandle: string;
  parentId: string | null;
  createdById: string;
  updatedAt: string;
  /** Folders, owner only: this folder carries a grant of its own. */
  shared?: boolean;
  sizeBytes?: number;
  mimeType?: string;
  status?: 'PENDING' | 'READY';
  indexStatus?: SpaceIndexStatus;
  /** Not in listings: why the file is not searchable, in a sentence for the person looking at it. */
  indexError?: string | null;
  /** In the files feed: when the current version was last handed to the index. */
  indexRequestedAt?: string | null;
  versionId?: string | null;
  updatedById?: string;
  /** The email of whoever last changed it. */
  modifiedBy?: string | null;
}

export interface SpaceCrumb {
  id: string;
  name: string;
  address: string;
}

export interface SpaceListing {
  folder: SpaceNodeView | null;
  space: { handle: string; ownerEmail: string; owner: boolean };
  /** From where the caller's view begins — their own root, or the folder shared with them — down to this one. */
  breadcrumbs: SpaceCrumb[];
  /**
   * `add`: upload or create here. `edit`: rename, move or trash the children listed, and edit a file's
   * text. `delete`: rename, move or trash *this* folder. `manage`: share it (owner only, never a root).
   */
  can: { add: boolean; edit: boolean; delete: boolean; manage: boolean };
  /** Folders first, then files, each by name. */
  children: SpaceNodeView[];
  nextCursor: string | null;
}

export interface SharedSpace {
  handle: string;
  address: string;
  ownerEmail: string;
  ownerName: string | null;
  folders: Array<{ id: string; name: string; address: string; access: SpaceAccess }>;
}

export interface SpaceVersionView {
  id: string;
  sizeBytes: number;
  sha256: string | null;
  mimeType: string;
  createdById: string;
  createdByEmail: string | null;
  createdVia: SpaceChannel;
  createdAt: string;
  current: boolean;
}

/**
 * An archive (zip, 7z, tar, gzip/bzip2/xz tar) unpacked into its space by the spaces worker — the
 * server's `SpaceUnpackView`. Absent for a file that is not an archive, or one not yet found.
 */
export interface SpaceUnpack {
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'STOPPED' | 'FAILED' | 'SUPERSEDED';
  /** The folder it unpacks into; null until a run starts. */
  into: string | null;
  /** Files in place from it so far. */
  files: number;
  error: string | null;
  updatedAt: string;
  finishedAt: string | null;
}

export type SpaceFileDetail = SpaceNodeView & { versions: SpaceVersionView[]; unpacking?: SpaceUnpack };

export interface SpaceGrantView {
  folderId: string;
  folderAddress: string;
  email: string | null;
  name: string | null;
  userId: string;
  access: 'viewer' | 'editor';
  expiresAt: string | null;
  expired: boolean;
}

/** A share change asked for over MCP, waiting for the space's owner to approve or decline it. */
export interface SpaceShareRequest {
  id: string;
  folderId: string;
  folderAddress: string;
  folderName: string;
  userId: string;
  email: string | null;
  name: string | null;
  /** `none` asks for their access to be taken away. */
  access: 'viewer' | 'editor' | 'none';
  /** What they hold on the folder now, directly. */
  currentAccess: 'viewer' | 'editor' | null;
  grantExpiresAt: string | null;
  via: SpaceChannel;
  /** The asking client's User-Agent. */
  userAgent: string | null;
  status: 'proposed' | 'approved' | 'declined' | 'expired';
  createdAt: string;
  expiresAt: string;
  decidedAt: string | null;
}

export interface SpaceUploadTicket {
  file: SpaceNodeView;
  versionId: string;
  /** A presigned PUT straight to storage — another origin, so the session cookie does not go with it. */
  url: string;
  /** Send exactly these, except `content-length`, which the browser refuses to let a page set. */
  headers: Record<string, string>;
  expiresAt: string;
}

export interface SpaceUploadRequestFile {
  /** Relative to the target folder; may contain `/` for a folder drop, with `createFolders`. */
  path: string;
  size: number;
  contentType: string;
  /** Hex. Signed into the PUT when given, so storage itself refuses corrupted bytes. */
  sha256?: string;
}

export type SpaceUploadResult =
  | { path: string; ok: true; ticket: SpaceUploadTicket }
  | { path: string; ok: false; error: { status: number; code: string; message: string } };

export interface SpaceActivityRow {
  id: string;
  spaceId: string;
  actorId: string;
  actorEmail: string | null;
  /** One of SPACE_ACTIVITY_ACTIONS in @repo/shared-types. A string here so a new action still renders. */
  action: string;
  folderId: string | null;
  fileId: string | null;
  via: SpaceChannel;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

export interface SpaceTrashRow {
  kind: 'folder' | 'file';
  id: string;
  name: string;
  parentId: string;
  deletedAt: string;
  purgeAfter: string | null;
  address: string;
}

export interface SpaceTrashed {
  id: string;
  address: string;
  purgeAfter: string | null;
}

export interface SpaceDownload {
  url: string;
  expiresAt: string;
  name: string;
  sizeBytes: number;
  mimeType: string;
  inline: boolean;
}

export interface SpaceText {
  content: string;
  versionId: string;
  mimeType: string;
  sizeBytes: number;
  /** Render as markdown: a note, or what a deck or a workbook is extracted as. */
  markdown: boolean;
  /** Read out of a document (pdf, docx, pptx, xlsx) when it was indexed — not editable. */
  extracted: boolean;
  /** Only the beginning of a longer extracted text. */
  truncated: boolean;
}

export interface SpaceSearchHit {
  file: SpaceNodeView;
  /** Who wrote the current version, by email. */
  writtenBy: string | null;
  /** The headings over the passage that matched, with `<<`/`>>` around matched words; null for a match on the name alone. */
  heading: string | null;
  /** A `ts_headline` passage with `<<`/`>>` around the matched words; for a match by meaning alone, the passage's start, unmarked; null for a match on the name alone. */
  snippet: string | null;
  /** Its text or headings matched the words. */
  textMatch: boolean;
  nameMatch: boolean;
  /** Every word asked for, in the name or in one passage. */
  allTerms: boolean;
  /** One of its passages is near the search in meaning. */
  semantic: boolean;
}

export interface SpaceSearchResult {
  query: string;
  /** Whether search by meaning ran (false where nothing in reach has been read for meaning yet). */
  semantic: boolean;
  hits: SpaceSearchHit[];
}

export interface SpaceGrepResult {
  pattern: string;
  hits: Array<{ file: SpaceNodeView; writtenBy: string | null; heading: string | null; line: string }>;
}

/** A page of the files feed: files you can see, most recently changed first. */
export interface SpaceFileFeed {
  files: SpaceNodeView[];
  /** Every file you can see by index state — never narrowed by the `status` asked for. */
  counts: Record<SpaceIndexStatus, number>;
  nextCursor: string | null;
}

/**
 * A spaces error, carrying the sentence the server wrote for a person.
 *
 * `request()` above throws `"<status>: <raw body>"`, which for a JSON error puts braces and field names
 * in front of the one thing worth showing. Every spaces error is `{ statusCode, code, message }` with
 * `message` already phrased for whoever is looking — "Alice's space is full", "A file named notes.md is
 * in the way" — so this keeps it whole, and keeps `status`/`code` beside it for the cases the page
 * acts on (a 409 offering Replace, a stale `ifVersion` offering Reload).
 */
export class SpaceApiError extends Error {
  readonly status: number;
  /** `not_found`, `conflict`, `quota_exceeded`, `refused`, `storage_unavailable`; empty for a 400. */
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'SpaceApiError';
    this.status = status;
    this.code = code;
  }
}

/** Reads a failed spaces response into a SpaceApiError. A 400's `message` may be a list — one per field. */
export async function spaceErrorFrom(res: Response): Promise<SpaceApiError> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { code?: unknown; message?: unknown };
    const message = Array.isArray(body.message)
      ? body.message.filter((m): m is string => typeof m === 'string').join('; ')
      : typeof body.message === 'string'
        ? body.message
        : '';
    if (message) return new SpaceApiError(res.status, typeof body.code === 'string' ? body.code : '', message);
  } catch {
    // Not JSON — a proxy's error page, most likely. Fall through to the status line.
  }
  return new SpaceApiError(res.status, '', text.trim() && text.length < 300 ? text.trim() : `${res.status} ${res.statusText || 'Request failed'}`);
}

/** The sentence to show for anything thrown by a spaces call (or by anything else). */
export function spaceErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

async function spacesRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (!res.ok) throw await spaceErrorFrom(res);
  return res.json() as Promise<T>;
}

const json = (method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

export const spacesApi = {
  /** Your own space. Creates it on first use, so it never 404s for a signed-in person. */
  me: () => spacesRequest<SpaceSummary>('/spaces/me'),
  shared: () => spacesRequest<SharedSpace[]>('/spaces/shared'),

  folder: (id: string, params?: { cursor?: string | null; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return spacesRequest<SpaceListing>(`/spaces/folders/${encodeURIComponent(id)}${query ? `?${query}` : ''}`);
  },
  createFolder: (body: { parentId: string; name: string }) => spacesRequest<SpaceNodeView>('/spaces/folders', json('POST', body)),
  /** Rename and/or move. */
  updateFolder: (id: string, body: { name?: string; parentId?: string }) =>
    spacesRequest<SpaceNodeView>(`/spaces/folders/${encodeURIComponent(id)}`, json('PATCH', body)),
  trashFolder: (id: string) => spacesRequest<SpaceTrashed>(`/spaces/folders/${encodeURIComponent(id)}`, json('DELETE')),
  restoreFolder: (id: string) => spacesRequest<SpaceNodeView>(`/spaces/folders/${encodeURIComponent(id)}/restore`, json('POST', {})),

  /** Owner only. `requests` are share changes asked for over MCP and not yet approved. */
  grants: (folderId: string) =>
    spacesRequest<{ direct: SpaceGrantView[]; inherited: SpaceGrantView[]; requests: SpaceShareRequest[] }>(
      `/spaces/folders/${encodeURIComponent(folderId)}/grants`,
    ),
  /** Share requests waiting for you, newest first. */
  shareRequests: () => spacesRequest<SpaceShareRequest[]>('/spaces/proposals'),
  /** Applies it — the grant, change or revoke it asked for — checked as a grant made here would be. */
  approveShareRequest: (id: string) => spacesRequest<SpaceShareRequest>(`/spaces/proposals/${encodeURIComponent(id)}/approve`, json('POST', {})),
  declineShareRequest: (id: string) => spacesRequest<SpaceShareRequest>(`/spaces/proposals/${encodeURIComponent(id)}/decline`, json('POST', {})),
  /** Granting someone who already has access changes it. */
  grant: (folderId: string, body: { email: string; access: 'viewer' | 'editor'; expiresAt?: string | null }) =>
    spacesRequest<SpaceGrantView>(`/spaces/folders/${encodeURIComponent(folderId)}/grants`, json('POST', body)),
  revoke: (folderId: string, userId: string) =>
    spacesRequest<{ removed: boolean }>(
      `/spaces/folders/${encodeURIComponent(folderId)}/grants/${encodeURIComponent(userId)}`,
      json('DELETE'),
    ),

  /** At most 200 files per call. */
  requestUploads: (body: {
    folderId: string;
    files: SpaceUploadRequestFile[];
    onConflict?: SpaceOnConflict;
    createFolders?: boolean;
  }) => spacesRequest<{ uploads: SpaceUploadResult[] }>('/spaces/uploads', json('POST', body)),
  /** After the PUT finished: the server checks the bytes arrived, then charges quota. */
  completeUpload: (fileId: string, versionId?: string) =>
    spacesRequest<SpaceNodeView>(`/spaces/files/${encodeURIComponent(fileId)}/complete`, json('POST', versionId ? { versionId } : {})),

  file: (id: string) => spacesRequest<SpaceFileDetail>(`/spaces/files/${encodeURIComponent(id)}`),
  updateFile: (id: string, body: { name?: string; folderId?: string }) =>
    spacesRequest<SpaceNodeView>(`/spaces/files/${encodeURIComponent(id)}`, json('PATCH', body)),
  trashFile: (id: string) => spacesRequest<SpaceTrashed>(`/spaces/files/${encodeURIComponent(id)}`, json('DELETE')),
  restoreFile: (id: string) => spacesRequest<SpaceNodeView>(`/spaces/files/${encodeURIComponent(id)}/restore`, json('POST', {})),

  /** A five-minute signed GET. `inline` only for pdf, png, jpeg, gif, webp and text/plain — anything else is a 422. */
  download: (id: string, params?: { inline?: boolean; version?: string }) => {
    const qs = new URLSearchParams();
    if (params?.inline) qs.set('inline', 'true');
    if (params?.version) qs.set('version', params.version);
    const query = qs.toString();
    return spacesRequest<SpaceDownload>(`/spaces/files/${encodeURIComponent(id)}/download${query ? `?${query}` : ''}`);
  },
  /**
   * A text file's content (up to `maxTextWriteBytes`), or a document's extracted text (its beginning, if
   * longer). A 409 while a document's text is still being read; a 422 with a sentence otherwise.
   */
  text: (id: string) => spacesRequest<SpaceText>(`/spaces/files/${encodeURIComponent(id)}/text`),
  /** Everything you can see, or beneath `folder`. */
  search: (params: { q: string; folder?: string | null; limit?: number }) => {
    const qs = new URLSearchParams({ q: params.q });
    if (params.folder) qs.set('folder', params.folder);
    if (params.limit) qs.set('limit', String(params.limit));
    return spacesRequest<SpaceSearchResult>(`/spaces/search?${qs.toString()}`);
  },
  /** Exact text, ignoring case, over what has been indexed. */
  grep: (params: { pattern: string; folder?: string | null; limit?: number }) => {
    const qs = new URLSearchParams({ pattern: params.pattern });
    if (params.folder) qs.set('folder', params.folder);
    if (params.limit) qs.set('limit', String(params.limit));
    return spacesRequest<SpaceGrepResult>(`/spaces/grep?${qs.toString()}`);
  },
  /** Files you can see, most recently changed first, at most 200 a page; `status` narrows the page to those index states. */
  recentFiles: (params?: { status?: SpaceIndexStatus[]; cursor?: string | null; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status?.length) qs.set('status', params.status.join(','));
    if (params?.cursor) qs.set('cursor', params.cursor);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return spacesRequest<SpaceFileFeed>(`/spaces/files${query ? `?${query}` : ''}`);
  },
  /** A 409 when `ifVersion` is no longer the current version — somebody saved in between. */
  writeContent: (id: string, body: { content: string; ifVersion?: string }) =>
    spacesRequest<SpaceNodeView>(`/spaces/files/${encodeURIComponent(id)}/content`, json('PUT', body)),

  /** Your own space, newest first. Page with the last row's `createdAt` as `before`. */
  activity: (params?: { before?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.before) qs.set('before', params.before);
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString();
    return spacesRequest<SpaceActivityRow[]>(`/spaces/activity${query ? `?${query}` : ''}`);
  },
  trash: (params?: { limit?: number }) =>
    spacesRequest<SpaceTrashRow[]>(`/spaces/trash${params?.limit ? `?limit=${params.limit}` : ''}`),
};
